/**
 * 근로관리 > 내 승인 요청 페이지
 *
 * V155: MVP 구현 (탭 방식, 상세 모달, 취소 기능)
 * V155.1: CF 조회 전환
 * V156: UI 전면 개편 (단일 리스트, 필터, 삭제)
 * V156.2: 최종 개편
 * - 체크박스 다중 선택
 * - 추가/삭제 버튼
 * - 검색창 (유형 + 날짜)
 * - 테이블 컬럼 확장 (신청자, 요청일시 초단위, 승인일시, 내용)
 * - 추가 모달 (연장근무, 출퇴근 수기조정)
 *
 * V2: 휴일근무 신청 CTA 연결
 * - URL query parameter ?type=holidayWork 시 추가 모달 자동 오픈 + 휴일근무 선택
 */

import React, { useState, useCallback, useEffect } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { useSearchParams } from 'react-router-dom';  // V2: query param 읽기
// V188: navigate 제거 - 휴가신청 팝업 내부 완결
import { useMyApprovalRequests } from '../../shared/hooks/useApprovalRequests';
import { formatCompOffDaysHours, formatCompOffDelta } from '../../shared/utils/formatCompOff';  // V78.1
import { useApprovalMutations } from '../../shared/hooks/useApprovalMutations';
import { useOvertimeMutations } from '../../shared/hooks/useOvertimeMutations';
import { useAssignableProjects } from '../../shared/hooks/useAssignableProjects';  // V121: 프로젝트 목록 조회
import { useAttendanceMutations } from '../../shared/hooks/useAttendanceMutations';
import { useTodayAttendance } from '../../shared/hooks/useAttendance';  // V190: 정정 모드 표시용
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useApprovedWorkSchedule } from '../../shared/hooks/useApprovedWorkSchedule';  // V175.1
import { useLeaveMutations } from '../../shared/hooks/useLeaveMutations';  // V188: 휴가신청 팝업 추가
import { useLeaveTypes } from '../../shared/hooks/useLeaveTypes';  // V188: 휴가유형 조회
import { filterVisibleLeaveTypes } from '../../shared/utils/leaveTypeEligibility';  // V69: 자격 필터 SSoT
import { useLeaveUpload, LeaveAttachment } from '../../shared/hooks/useLeaveUpload';  // V70: 첨부파일 업로드
import { useMyLeaveBalance } from '../../shared/hooks/useLeaveBalances';  // V62: CN 연차 선사용 금지 FE 차단
import { formatDateTimeByCompanyCode } from '../../shared/utils/dateUtils';
import { LeaveUnit, LeaveTypeCode, LeaveType } from '../../shared/types/leaveType';  // V188
import { getLeaveUnitLabel } from '../../shared/utils/leaveUnitLabel';  // V69
import { getApprovalProjectLabel, isProjectSelectableApprovalType, withApprovalProjectPrefix } from '../../shared/utils/approvalProjectLabel';  // V70.7
import { DEFAULT_ALLOWED_UNITS } from '../../shared/constants/leaveUnits';  // V282-1
import { LeaveRequestInput } from '../../shared/types/leaveRequest';  // V188
import {
  createLeaveOfAbsenceRequest,
  calcMaxExpectedReturnDate,
  LOA_TYPE_LABELS,
  LOA_TYPE_MAX_LABELS,
  type LeaveOfAbsenceType,
} from '../../shared/hooks/useLeaveOfAbsence';  // V66 Phase 4: 본인 휴직 신청
import {
  ApprovalRequest,
  ApprovalRequestType,
  ApprovalRequestStatus,
} from '../../shared/types/approvalRequest';
import {
  X,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  RefreshCw,
} from 'lucide-react';

// ============================================
// V60.6.2: 대체휴무 시간단위 헬퍼 (근무시간 SSoT 기반)
// ============================================

import { WorkSchedule } from '../../shared/types/workSchedule';

/**
 * V60.6.2: workSchedule에서 특정 날짜의 근무시간 조회
 */
function getScheduleTimesForDate(
  ws: WorkSchedule | null,
  dateStr: string
): { startTime: string; endTime: string; lunchStart: string; lunchEnd: string } | null {
  if (!ws) return null;
  const override = ws.dailyOverrides?.[dateStr];
  return {
    startTime: override?.startTime ?? ws.base.startTime,
    endTime: override?.endTime ?? ws.base.endTime,
    lunchStart: override?.lunchStart ?? ws.base.lunchStart,
    lunchEnd: override?.lunchEnd ?? ws.base.lunchEnd,
  };
}

/**
 * V62: 점심시간을 건너뛰어 종료시간 계산
 * workHours = 실제 사용(근무)시간, 점심 구간은 자동 스킵
 */
function calcEndTimeSkippingBreaks(
  startTime: string,
  workHours: number,
  lunchStart?: string,
  lunchEnd?: string
): string {
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const fromMin = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

  const startMin = toMin(startTime);
  const requiredMin = workHours * 60;

  if (!lunchStart || !lunchEnd) {
    return fromMin(startMin + requiredMin);
  }

  const lStartMin = toMin(lunchStart);
  const lEndMin = toMin(lunchEnd);

  if (startMin >= lEndMin) {
    return fromMin(startMin + requiredMin);
  }
  if (startMin >= lStartMin) {
    return fromMin(lEndMin + requiredMin);
  }
  const beforeLunch = lStartMin - startMin;
  if (beforeLunch >= requiredMin) {
    return fromMin(startMin + requiredMin);
  }
  const remaining = requiredMin - beforeLunch;
  return fromMin(lEndMin + remaining);
}

/**
 * V62: 근무시간 범위 내에서 시작시간 옵션 생성 (30분 단위)
 * 점심시간 스킵 기반 종료시간이 scheduleEnd 이내인 시작시간만 포함
 */
function generateStartTimeOptions(
  scheduleStart: string,
  scheduleEnd: string,
  hours: number,
  lunchStart?: string,
  lunchEnd?: string
): string[] {
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const startMin = toMin(scheduleStart);
  const endMin = toMin(scheduleEnd);
  const options: string[] = [];
  for (let m = startMin; m < endMin; m += 30) {
    const fromMin = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
    const startStr = fromMin(m);
    const calcEnd = calcEndTimeSkippingBreaks(startStr, hours, lunchStart, lunchEnd);
    if (toMin(calcEnd) <= endMin) {
      options.push(startStr);
    }
  }
  return options;
}

// ============================================
// 타입/라벨 매핑
// ============================================

/**
 * V198: BE handler.ts SSoT 기준으로 타입 라벨 동기화
 * V55: 연장근무 취소 타입 추가
 */
const APPROVAL_TYPE_LABELS: Record<ApprovalRequestType, string> = {
  leave: '휴가',
  leaveCancel: '휴가취소',              // V185
  workSchedule: '근로일정(월)',
  workScheduleChange: '근로일정 변경',
  workScheduleBulkChange: '근로일정 일괄수정',  // V66
  attendanceManual: '출퇴근 수기조정',
  attendanceManualCancel: '출퇴근조정 취소',  // V198
  overtime: '연장근무',
  overtimeCancel: '연장근무 취소',
  weekendWork: '휴일근무',
  weekendWorkCancel: '휴일근무 취소',    // V55
  holidayWork: '휴일근무',               // V247: 기존 데이터 하위호환
  holidayWorkCancel: '휴일근무 취소',    // V247: 기존 데이터 하위호환
  transfer: '전배',                      // V198
  leaveOfAbsence: '휴직 신청',          // V66 Phase 4
  leaveOfAbsenceUpdate: '휴직 수정 및 연장', // V66
};

/**
 * V73: 휴가 요청 유형 칼럼에 실제 휴가 종류 표시
 */
function getTypeDisplayLabel(type: ApprovalRequestType, requestData?: any): string {
  if (type === 'leave') {
    return requestData?.leaveType || APPROVAL_TYPE_LABELS.leave;
  }
  if (type === 'leaveCancel') {
    const leaveName = requestData?.leaveTypeName || requestData?.leaveType || '휴가';
    return `${leaveName} 취소`;
  }
  return APPROVAL_TYPE_LABELS[type] || type;
}

/**
 * V55.1: 상태 라벨 개선
 * - orgApproved → "1차 승인" (더 직관적인 표현)
 * - approved → "최종 승인"
 */
const STATUS_LABELS: Record<ApprovalRequestStatus, string> = {
  pending: '미승인',
  orgApproved: '1차 승인',  // V55.1: HR승인대기 → 1차 승인
  approved: '최종 승인',    // V55.1: 승인 → 최종 승인
  approved_scheduled: '예약대기',
  applied: '적용완료',
  rejected: '반려',
  canceled: '취소',
  revoked: '취소됨',  // V179.1: 승인된 OT 취소
};

const STATUS_COLORS: Record<ApprovalRequestStatus, { bg: string; text: string }> = {
  pending: { bg: 'var(--color-orange-100)', text: 'var(--color-orange-800)' },
  orgApproved: { bg: 'var(--color-blue-100)', text: 'var(--color-blue-800)' },  // V50: HR 대기
  approved: { bg: 'var(--color-green-100)', text: 'var(--color-green-800)' },
  approved_scheduled: { bg: 'var(--color-orange-100)', text: 'var(--color-orange-800)' },
  applied: { bg: 'var(--color-green-100)', text: 'var(--color-green-800)' },
  rejected: { bg: 'var(--color-red-100)', text: 'var(--color-red-800)' },
  canceled: { bg: 'var(--color-gray-100)', text: 'var(--color-gray-600)' },
  revoked: { bg: 'var(--color-purple-100)', text: 'var(--color-purple-800)' },  // V179.1
};

/**
 * V55.4: cancelStatus 기반 라벨 (cancelRequested=true일 때 사용)
 */
const CANCEL_STATUS_LABELS: Record<string, string> = {
  pending: '취소승인대기',
  orgApproved: '인사팀 취소승인대기',
  cancelled: '취소완료',
  rejected: '취소반려',
};

const CANCEL_STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'var(--color-orange-100)', text: 'var(--color-orange-800)' },
  orgApproved: { bg: 'var(--color-blue-100)', text: 'var(--color-blue-800)' },
  cancelled: { bg: 'var(--color-gray-100)', text: 'var(--color-gray-600)' },
  rejected: { bg: 'var(--color-red-100)', text: 'var(--color-red-800)' },
};

/**
 * V55.4: 상태 라벨 및 스타일 결정 (cancelStatus 우선)
 */
function getStatusDisplay(request: ApprovalRequest): { label: string; bg: string; text: string } {
  // cancelRequested=true이고 cancelStatus가 있으면 취소 상태 표시
  if ((request as any).cancelRequested === true || (request as any).cancelStatus) {
    const cancelStatus = (request as any).cancelStatus || 'pending';
    return {
      label: CANCEL_STATUS_LABELS[cancelStatus] || '취소승인대기',
      ...(CANCEL_STATUS_COLORS[cancelStatus] || CANCEL_STATUS_COLORS.pending),
    };
  }
  // 일반 상태 표시
  return {
    label: STATUS_LABELS[request.status] || request.status,
    ...(STATUS_COLORS[request.status] || STATUS_COLORS.pending),
  };
}

// V156.2: 연도 옵션 (현재 연도부터 3년 전)
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [
  { value: currentYear, label: `${currentYear}년` },
  ...Array.from({ length: 3 }, (_, i) => ({
    value: currentYear - i - 1,
    label: `${currentYear - i - 1}년`,
  })),
];

// ============================================
// 유틸리티 함수
// ============================================

/**
 * requestData에서 대상 일자/월 추출
 */
function extractTargetDate(type: ApprovalRequestType, requestData: any): string {
  if (!requestData) return '-';

  switch (type) {
    case 'workSchedule':
      if (requestData.month && typeof requestData.month === 'string') {
        return requestData.month; // YYYY-MM
      }
      return '-';

    case 'workScheduleChange':
    case 'attendanceManual':
    case 'overtime':
    case 'weekendWork':
    case 'holidayWork':
      return requestData.date || '-';

    case 'workScheduleBulkChange': {
      // V66: 다중 날짜 → min ~ max 범위 (없으면 month)
      const dates: string[] = Array.isArray(requestData.dates) ? [...requestData.dates] : [];
      if (dates.length === 0) return requestData.month || '-';
      dates.sort();
      const minD = dates[0];
      const maxD = dates[dates.length - 1];
      return minD === maxD ? minD : `${minD} ~ ${maxD}`;
    }

    case 'leave':
      if (requestData.startDate && requestData.endDate) {
        if (requestData.startDate === requestData.endDate) {
          return requestData.startDate;
        }
        return `${requestData.startDate} ~ ${requestData.endDate}`;
      }
      return requestData.date || '-';

    case 'leaveCancel':
      // V185: 휴가 취소 요청 - 날짜 표시
      if (requestData.startDate && requestData.endDate) {
        if (requestData.startDate === requestData.endDate) {
          return requestData.startDate;
        }
        return `${requestData.startDate} ~ ${requestData.endDate}`;
      }
      return requestData.startDate || requestData.date || '-';

    case 'attendanceManualCancel':
      // V198: 출퇴근 수기조정 취소
      return requestData.date || requestData.targetDate || '-';

    case 'transfer':
      // V198: 전배 - effectiveDate 표시
      return requestData.effectiveDate || requestData.date || '-';

    default:
      // V198: fallback
      return requestData.date || requestData.targetDate || requestData.startDate || '-';
  }
}

/**
 * requestData에서 내용/사유 추출
 */
function extractContent(type: ApprovalRequestType, requestData: any): string {
  if (!requestData) return '-';

  switch (type) {
    case 'overtime':
    case 'weekendWork':
    case 'holidayWork':
      const timeRange = requestData.startTime && requestData.endTime
        ? `${requestData.startTime}~${requestData.endTime}`
        : '';
      return requestData.reason
        ? `${timeRange} ${requestData.reason}`.trim()
        : timeRange || '-';

    case 'attendanceManual':
      return requestData.reason || '출퇴근 수정 요청';

    case 'workScheduleChange': {
      // V60: BE 실제 필드명에 맞춤 (originalSchedule, changes)
      const origS = requestData.originalSchedule;
      const chgS = requestData.changes;
      if (origS && chgS) {
        const bef = `${origS.startTime}~${origS.endTime}`;
        const aftS = chgS.startTime || origS.startTime;
        const aftE = chgS.endTime || origS.endTime;
        return `${bef} → ${aftS}~${aftE}`;
      }
      return requestData.reason || '근로일정 변경 요청';
    }

    case 'workScheduleBulkChange': {
      // V66: 다중 날짜 일괄 변경 요약
      const cnt = Array.isArray(requestData.dates) ? requestData.dates.length : 0;
      const ch = requestData.changes || {};
      const after = (ch.startTime && ch.endTime) ? `${ch.startTime}~${ch.endTime}` : '';
      const tpl = requestData.templateName ? `[${requestData.templateName}]` : '';
      const perDateCnt = requestData.perDateChanges
        ? Object.keys(requestData.perDateChanges).length : 0;
      const perDateBadge = perDateCnt > 0 ? `요일별 차등 ${perDateCnt}일` : '';
      const summary = [tpl, after, cnt > 0 ? `${cnt}일` : '', perDateBadge].filter(Boolean).join(' ').trim();
      return [summary, requestData.reason].filter(Boolean).join(' / ') || '-';
    }

    case 'workSchedule':
      if (requestData.base) {
        return `${requestData.base.startTime}~${requestData.base.endTime}`;
      }
      return '근로일정 등록';

    case 'leave':
      return requestData.reason || requestData.leaveTypeName || '휴가';

    case 'leaveCancel':
      // V185: 휴가 취소 요청 - 휴가 유형명 표시
      return `(취소) ${requestData.leaveTypeName || '휴가'}`;

    case 'attendanceManualCancel':
      // V198: 출퇴근 수기조정 취소
      return `(취소) ${requestData.reason || '출퇴근 조정 취소'}`;

    case 'transfer':
      // V198: 전배
      if (requestData.fromOrgUnitName && requestData.toOrgUnitName) {
        return `${requestData.fromOrgUnitName} → ${requestData.toOrgUnitName}`;
      }
      return requestData.reason || '전배 요청';

    case 'leaveOfAbsence': {
      // V66 Phase 4: 휴직 신청 — 종류 + 기간 + 사유 (목록 표시)
      const typeName = requestData.typeName || '휴직';
      const sd = requestData.startDate ? requestData.startDate.replace(/-/g, '.') : '';
      const ed = requestData.expectedReturnDate ? requestData.expectedReturnDate.replace(/-/g, '.') : '';
      const period = sd && ed ? `(${sd} ~ ${ed})` : (sd ? `(${sd} ~ )` : '');
      const head = period ? `${typeName} ${period}` : typeName;
      return [head, requestData.reason].filter(Boolean).join(' - ') || '-';
    }

    case 'leaveOfAbsenceUpdate': {
      // V66: 휴직 수정 및 연장 — 변경 전 → 변경 후
      const orig = requestData.original || {};
      const upd = requestData.updated || {};
      const typeName = upd.typeName || orig.typeName || '휴직';
      const fmt = (s: any) => (s ? String(s).replace(/-/g, '.') : '');
      const o = `${fmt(orig.startDate) || '?'} ~ ${fmt(orig.expectedReturnDate) || '미정'}`;
      const u = `${fmt(upd.startDate) || '?'} ~ ${fmt(upd.expectedReturnDate) || '미정'}`;
      const head = `${typeName} / ${o} → ${u}`;
      return requestData.changeReason
        ? `${head} / 변경사유: ${requestData.changeReason}`
        : head;
    }

    default:
      // V198: fallback
      return requestData.reason || '-';
  }
}

/**
 * V170.1: Timestamp를 회사 타임존 기준 문자열로 변환 (초 단위 포함)
 * 이제 formatDateTimeByCompanyCode 사용 권장
 * @deprecated formatDateTimeByCompanyCode(timestamp, companyCode, true) 사용
 */
function formatTimestampWithSeconds(timestamp: any, companyCode?: string | null): string {
  return formatDateTimeByCompanyCode(timestamp, companyCode, true);
}

// ============================================
// 메인 컴포넌트
// ============================================

export function WorkMyApprovalsPage() {
  // V170.1: companyCode 기반 시간 표시를 위해
  const { user } = useCurrentUser();
  const companyCode = user?.profile?.companyCode;

  // V2: URL query parameter 읽기 (?type=holidayWork)
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get('type') as ApprovalRequestType | null;

  // 필터 상태
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [searchQuery, setSearchQuery] = useState('');
  // V243: debounce된 검색어 (BE 검색용)
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // 다중 선택 상태
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 모달 상태
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ApprovalRequest | null>(null);

  // 삭제 확인 모달
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // V55: 승인된 요청 취소 모달
  const [showCancelApprovedModal, setShowCancelApprovedModal] = useState(false);
  const [cancelApprovedError, setCancelApprovedError] = useState<string | null>(null);

  // V191: 토스트 상태 (모달 닫혀도 유지)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // V191: 토스트 자동 dismiss (1.5초)
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 1500);
    return () => clearTimeout(timer);
  }, [toast]);

  // V2: URL query parameter로 type 지정 시 추가 모달 자동 오픈
  // 예: /work/my-approvals?type=holidayWork → 추가 모달 열고 휴일근무 선택
  useEffect(() => {
    // V247: 'weekend' 추가, 'holidayWork' 하위호환 유지
    if (initialType && ['overtime', 'weekend', 'weekendWork', 'holidayWork', 'attendanceManual', 'leave'].includes(initialType)) {
      setShowAddModal(true);
      // URL에서 type param 제거 (새로고침 시 다시 열리지 않도록)
      setSearchParams({}, { replace: true });
    }
  }, [initialType, setSearchParams]);

  // V237.1: BE cursor 기반 페이지네이션 (10개/페이지)
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  // cursorStack[i] = i+2 페이지를 조회할 때 사용할 cursor (즉, 이전 페이지의 nextCursor)
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const { requests, isLoading, error, refetch, hasMore, nextCursor, fetchPage, total } = useMyApprovalRequests({
    year: selectedYear,
    pageSize: PAGE_SIZE,
    query: debouncedQuery || undefined,  // V243: BE 검색
  });

  // V243: 검색어 debounce (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // V243: debounce된 검색어 변경 시 1페이지 리셋
  useEffect(() => {
    setCurrentPage(1);
    setCursorStack([]);
  }, [debouncedQuery]);

  // V193: 삭제 대신 취소(soft delete) 사용 - approvalRequests.status를 'canceled'로 변경
  // V55: requestCancelApproval 추가 - 승인된 요청의 취소 승인요청 생성
  const { cancelRequest, requestCancelApproval, isProcessing: isDeleting } = useApprovalMutations();

  // V243: BE에서 검색 처리 → client-side filter 불필요, BE 결과 직접 사용
  const filteredRequests = requests;

  // V237.1: cursorStack[i] = page i+2 를 조회하는 cursor
  // page 1: cursor=undefined, page 2: cursorStack[0], page 3: cursorStack[1], ...

  // V243: 검색 모드 여부
  const isSearchMode = !!debouncedQuery.trim();

  // V243: 다음 페이지 이동 (검색 모드: offset / 일반: cursor)
  const handleNextPage = useCallback(async () => {
    if (!hasMore || isLoading) return;

    if (isSearchMode) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      setSelectedIds(new Set());
      await fetchPage(undefined, nextPage);
    } else {
      if (!nextCursor) return;
      setCursorStack((prev) => {
        if (prev.length < currentPage) return [...prev, nextCursor];
        return prev;
      });
      setCurrentPage((p) => p + 1);
      setSelectedIds(new Set());
      await fetchPage(nextCursor);
    }
  }, [hasMore, nextCursor, isLoading, currentPage, fetchPage, isSearchMode]);

  // V243: 이전 페이지 이동 (검색 모드: offset / 일반: cursor)
  const handlePrevPage = useCallback(async () => {
    if (currentPage <= 1 || isLoading) return;

    if (isSearchMode) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);
      setSelectedIds(new Set());
      await fetchPage(undefined, prevPage);
    } else {
      const targetPage = currentPage - 1;
      const targetCursor = targetPage <= 1 ? undefined : cursorStack[targetPage - 2];
      setCurrentPage(targetPage);
      setSelectedIds(new Set());
      await fetchPage(targetCursor);
    }
  }, [currentPage, cursorStack, isLoading, fetchPage, isSearchMode]);

  // V237.1: 전체 선택 / 해제 (현재 페이지 내에서만)
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredRequests.map((r) => r.id)));
    } else {
      setSelectedIds(new Set());
    }
  }, [filteredRequests]);

  // 개별 선택
  const handleSelectOne = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // V193: 삭제 → 취소 처리 (soft delete)
  // approvalRequests.status를 'canceled'로 변경하여 승인자 화면에서도 사라지게 함
  const handleDelete = async () => {
    setDeleteError(null);

    // V55.3: 선택된 항목 중 pending만 취소 (혼합 선택 허용)
    // V55.10: 전체 타입 A 재활성화 통일 - cancelRequested/cancelStatus로 판단
    const pendingItems = requests.filter(
      (r) =>
        selectedIds.has(r.id) &&
        r.status === 'pending' &&
        // V55.10: 취소승인대기 상태는 철회 불가 (전체 타입 공통)
        !((r as any).cancelRequested === true || (r as any).cancelStatus) &&
        !r.type.endsWith('Cancel')  // 기존 B 타입 하위호환
    );

    if (pendingItems.length === 0) {
      // V55.10: 취소승인대기 항목만 선택된 경우 안내 메시지
      const cancelPendingItems = requests.filter(
        (r) =>
          selectedIds.has(r.id) &&
          r.status === 'pending' &&
          ((r as any).cancelRequested === true || (r as any).cancelStatus || r.type.endsWith('Cancel'))
      );
      if (cancelPendingItems.length > 0) {
        setDeleteError('취소 승인 대기 중에는 철회할 수 없습니다.');
      } else {
        setDeleteError('취소 가능한 미승인 요청이 없습니다.');
      }
      return;
    }

    try {
      for (const item of pendingItems) {
        // V193: deleteRequest → cancelRequest로 변경
        await cancelRequest(item.id);
      }

      setSelectedIds(new Set());
      setShowDeleteConfirm(false);
      setToast({ type: 'success', message: `${pendingItems.length}건이 취소되었습니다.` });
      // V237.1: 취소 후 1페이지로 리셋
      setCurrentPage(1);
      setCursorStack([]);
      refetch();
    } catch (err: any) {
      setDeleteError(err.message || '취소 중 오류가 발생했습니다.');
    }
  };

  // V55.1: 승인된 요청 취소 처리 (approved + orgApproved)
  // V246: attendanceManual 제거 — 출퇴근 수기조정은 승인 후 취소요청 불가
  // V68: leaveOfAbsence 추가 — 미래 예정 휴직만 취소 허용 (아래 validation 에서 startDate 체크)
  const CANCEL_SUPPORTED_TYPES: ApprovalRequestType[] = ['leave', 'overtime', 'weekendWork', 'holidayWork', 'leaveOfAbsence'];
  const CANCEL_ALLOWED_STATUSES: ApprovalRequestStatus[] = ['approved', 'orgApproved'];

  /**
   * V55.4: 승인된 요청 취소 대상 필터 함수
   * - approved 상태만 (orgApproved는 휴가일 때 차단)
   * - 지원 타입 (Cancel 접미어 타입 제외)
   * - 이미 취소 요청 진행 중이 아닌 것
   * - cancelStatus가 있으면 취소 불가 (취소 진행/완료/반려 상태)
   * - V68: leaveOfAbsence 는 startDate > today (미래 예정) 만 허용
   */
  const isCancelableApprovedRequest = (r: ApprovalRequest): boolean => {
    // V55.4: cancelStatus가 있으면 이미 취소 처리된 것으로 간주
    if ((r as any).cancelStatus) return false;
    // V55.4: cancelRequested=true이면 취소요청 진행 중
    if (r.cancelRequested === true || r.cancelApprovalRequestId) return false;
    // Cancel 접미어 타입 제외
    if (r.type.endsWith('Cancel')) return false;
    // 지원 타입 체크
    if (!CANCEL_SUPPORTED_TYPES.includes(r.type)) return false;
    // V55.4: 휴가(leave)는 approved 상태에서만 취소 가능 (orgApproved 차단)
    if (r.type === 'leave' && r.status !== 'approved') return false;
    // V68: 휴직(leaveOfAbsence)은 approved + 미래 예정(startDate > today) 만 취소 허용.
    //   진행 중/종료된 휴직은 휴직 수정(연장/조기복직)으로 처리.
    if (r.type === 'leaveOfAbsence') {
      if (r.status !== 'approved') return false;
      const startDate = (r as any).startDate || (r as any).requestData?.startDate || '';
      if (!startDate) return false;
      const todayKst = new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);
      if (startDate <= todayKst) return false; // 이미 시작 또는 과거 — 미노출
    }
    // 다른 타입은 approved/orgApproved 허용
    return CANCEL_ALLOWED_STATUSES.includes(r.status);
  };

  const handleCancelApproved = async (reason: string) => {
    setCancelApprovedError(null);

    // V55.3: 통합 필터 함수 사용
    const selectedApprovedRequests = requests.filter(
      (r) => selectedIds.has(r.id) && isCancelableApprovedRequest(r)
    );

    if (selectedApprovedRequests.length === 0) {
      setCancelApprovedError('취소 가능한 승인된 요청이 없습니다.');
      return;
    }

    try {
      for (const item of selectedApprovedRequests) {
        await requestCancelApproval({
          originalApprovalRequestId: item.id,
          reason,
        });
      }

      setSelectedIds(new Set());
      setShowCancelApprovedModal(false);
      setToast({ type: 'success', message: `${selectedApprovedRequests.length}건의 취소 요청이 생성되었습니다.` });
      // V237.1: 취소 후 1페이지로 리셋
      setCurrentPage(1);
      setCursorStack([]);
      refetch();
    } catch (err: any) {
      setCancelApprovedError(err.message || '취소 요청 생성 중 오류가 발생했습니다.');
    }
  };

  // V237.1: 필터 변경 시 선택/페이지네이션 초기화
  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    setSelectedIds(new Set());
    setCurrentPage(1);
    setCursorStack([]);
    // year 변경 → fetchPage deps 변경 → useEffect 자동 재조회
  };

  // V243: 검색어 변경 (debounce를 통해 BE 검색 트리거)
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setSelectedIds(new Set());
  };

  // V191: 추가 성공/실패 시 토스트 표시 + 리프레시
  const handleAddSuccess = (message?: string) => {
    setShowAddModal(false);
    setToast({ type: 'success', message: message || '제출이 완료되었습니다.' });
    // V237.1: 추가 후 1페이지로 리셋
    setCurrentPage(1);
    setCursorStack([]);
    refetch();
  };

  const handleAddError = (errorMessage: string) => {
    setToast({ type: 'error', message: errorMessage });
  };

  // V237.1: 현재 페이지 데이터 기준 전체 선택 판단
  const isAllSelected = filteredRequests.length > 0 && filteredRequests.every((r) => selectedIds.has(r.id));

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: '50px' }}>
      {/* 페이지 제목 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '24px',
        }}
      >
        내 승인 요청
      </h1>

      {/* 상단 컨트롤 영역 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* 좌측: 연도 선택 + 추가/삭제 버튼 */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {/* 연도 선택 */}
          <select
            value={selectedYear}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--color-gray-300)',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer',
            }}
          >
            {YEAR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {/* 추가 버튼 */}
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '8px 14px',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} />
            추가
          </button>

          {/* V55.2: 미승인 요청 취소 버튼 - pending 상태만 대상 */}
          {/* V55.10: 전체 타입 A 재활성화 통일 - cancelRequested/cancelStatus로 판단 */}
          {(() => {
            const selectedPendingCount = requests.filter(
              (r) =>
                selectedIds.has(r.id) &&
                r.status === 'pending' &&
                // V55.10: 취소승인대기 상태는 철회 불가 (전체 타입 공통)
                !((r as any).cancelRequested === true || (r as any).cancelStatus) &&
                !r.type.endsWith('Cancel')  // 기존 B 타입 하위호환
            ).length;

            return selectedPendingCount > 0 ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 14px',
                  background: 'var(--color-red-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
                미승인 요청 취소 ({selectedPendingCount})
              </button>
            ) : null;
          })()}

          {/* V55.3: 승인된 요청 취소 버튼 - 통합 필터 함수 사용 */}
          {/* 선택된 항목 중 승인된 취소 대상이 있을 때만 노출 */}
          {(() => {
            const selectedApprovedCount = requests.filter(
              (r) => selectedIds.has(r.id) && isCancelableApprovedRequest(r)
            ).length;

            return selectedApprovedCount > 0 ? (
              <button
                onClick={() => setShowCancelApprovedModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 14px',
                  background: 'var(--color-orange-600)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <X size={16} />
                승인된 요청 취소 ({selectedApprovedCount})
              </button>
            ) : null;
          })()}

          {/* 새로고침 */}
          <button
            onClick={() => {
              setCurrentPage(1);
              setCursorStack([]);
              setSelectedIds(new Set());
              refetch();
            }}
            disabled={isLoading}
            style={{
              padding: '8px',
              background: 'var(--color-gray-100)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            title="새로고침"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* 우측: 검색창 */}
        <div style={{ position: 'relative' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-gray-400)',
            }}
          />
          <input
            type="text"
            placeholder="유형, 날짜로 검색..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{
              padding: '8px 12px 8px 36px',
              borderRadius: '6px',
              border: '1px solid var(--color-gray-300)',
              fontSize: '14px',
              width: '240px',
            }}
          />
        </div>
      </div>

      {/* V243: 결과 카운트 (검색 시 전체 결과 수 표시) */}
      <div style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '12px' }}>
        {debouncedQuery
          ? `검색 결과 ${total}건 중 ${filteredRequests.length}건 표시`
          : `${filteredRequests.length}건 표시`
        }
        {debouncedQuery && total >= 500 && (
          <span style={{ color: '#888', fontSize: '12px', marginLeft: '6px' }}>
            (최근 500건 기준 검색)
          </span>
        )}
      </div>

      {/* 로딩 */}
      {isLoading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
          로딩 중...
        </div>
      )}

      {/* 에러 */}
      {error && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-red-600)' }}>
          오류: {error.message}
        </div>
      )}

      {/* 테이블 */}
      {!isLoading && !error && (
        <>
          {filteredRequests.length > 0 ? (
            <>
              <div
                style={{
                  background: 'white',
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* 테이블 헤더 */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 120px 70px 80px 130px 150px 100px 150px 110px 1fr',
                    gap: '8px',
                    padding: '12px 16px',
                    background: 'var(--color-gray-50)',
                    borderBottom: '1px solid var(--color-gray-200)',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: 'var(--color-gray-600)',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                  </div>
                  <div style={{ textAlign: 'center' }}>유형</div>
                  <div style={{ textAlign: 'center' }}>사용 단위</div>
                  <div style={{ textAlign: 'center' }}>신청자</div>
                  <div style={{ textAlign: 'center' }}>날짜</div>
                  <div style={{ textAlign: 'center' }}>요청일시</div>
                  <div style={{ textAlign: 'center' }}>승인자</div>
                  <div style={{ textAlign: 'center' }}>승인일시</div>
                  <div style={{ textAlign: 'center' }}>승인상태</div>
                  <div style={{ textAlign: 'center' }}>내용</div>
                </div>

                {/* V237.1: 테이블 바디 - 현재 페이지 데이터 (서버 cursor 기반) */}
                {filteredRequests.map((request) => (
                  <ApprovalRequestRow
                    key={request.id}
                    request={request}
                    isSelected={selectedIds.has(request.id)}
                    onSelect={(checked) => handleSelectOne(request.id, checked)}
                    onViewDetail={() => setSelectedRequest(request)}
                    companyCode={companyCode}
                  />
                ))}
              </div>

              {/* V237.1: BE cursor 기반 페이지네이션 */}
              {(currentPage > 1 || hasMore) && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '24px',
                  }}
                >
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage <= 1 || isLoading}
                    style={{
                      padding: '8px 16px',
                      background: currentPage <= 1 ? 'var(--color-gray-100)' : 'white',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '6px',
                      cursor: currentPage <= 1 || isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      color: currentPage <= 1 ? 'var(--color-gray-400)' : 'var(--color-gray-700)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <ChevronLeft size={16} />
                    이전
                  </button>
                  <span style={{ fontSize: '14px', color: 'var(--color-gray-600)', fontWeight: 500 }}>
                    {currentPage} 페이지
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={!hasMore || isLoading}
                    style={{
                      padding: '8px 16px',
                      background: !hasMore ? 'var(--color-gray-100)' : 'white',
                      border: '1px solid var(--color-gray-300)',
                      borderRadius: '6px',
                      cursor: !hasMore || isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      color: !hasMore ? 'var(--color-gray-400)' : 'var(--color-gray-700)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    다음
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                background: 'white',
                padding: '60px 20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                textAlign: 'center',
                color: 'var(--color-gray-500)',
              }}
            >
              {searchQuery ? '검색 결과가 없습니다.' : '승인 요청 내역이 없습니다.'}
            </div>
          )}
        </>
      )}

      {/* 상세 모달 */}
      {selectedRequest && (
        <ApprovalDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          companyCode={companyCode}
        />
      )}

      {/* 추가 모달 */}
      {showAddModal && (
        <AddApprovalModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddSuccess}
          onError={handleAddError}
          initialCategory={(initialType === 'holidayWork' || initialType === 'weekendWork' ? 'weekend' : initialType) as AddCategory | undefined}
        />
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          count={requests.filter((r) => selectedIds.has(r.id) && r.status === 'pending').length}
          isDeleting={isDeleting}
          error={deleteError}
          onConfirm={handleDelete}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setDeleteError(null);
          }}
        />
      )}

      {/* V55.3: 승인된 요청 취소 모달 - 통합 필터 함수 사용 */}
      {showCancelApprovedModal && (
        <CancelApprovedModal
          count={requests.filter(
            (r) => selectedIds.has(r.id) && isCancelableApprovedRequest(r)
          ).length}
          isProcessing={isDeleting}
          error={cancelApprovedError}
          onConfirm={handleCancelApproved}
          onCancel={() => {
            setShowCancelApprovedModal(false);
            setCancelApprovedError(null);
          }}
        />
      )}

      {/* V191: 토스트 알림 */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            padding: '12px 24px',
            borderRadius: '8px',
            background: toast.type === 'success' ? '#10b981' : '#ef4444',
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// 행 컴포넌트
// ============================================

interface ApprovalRequestRowProps {
  request: ApprovalRequest;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onViewDetail: () => void;
  companyCode?: string | null; // V170.1
}

function ApprovalRequestRow({ request, isSelected, onSelect, onViewDetail, companyCode }: ApprovalRequestRowProps) {
  // V55.4: cancelStatus 우선 표시
  const statusDisplay = getStatusDisplay(request);
  const targetDate = extractTargetDate(request.type, request.requestData);
  // V294: 취소 요청 상태이면 취소 사유를 우선 표시
  // V70.7: 연장/휴일근무는 내용 앞에 [프로젝트] 표시
  const content = request.cancelRequested && request.cancelReason
    ? `(취소) ${request.cancelReason}`
    : withApprovalProjectPrefix(request.type, request.requestData, request.projectName, extractContent(request.type, request.requestData));

  // 승인일시 (approved/rejected)
  const approvalTimestamp = request.status === 'approved'
    ? request.approvedAt
    : request.status === 'rejected'
    ? request.rejectedAt
    : null;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 120px 70px 80px 130px 150px 100px 150px 110px 1fr',
        gap: '8px',
        padding: '12px 16px',
        borderBottom: '1px solid var(--color-gray-100)',
        alignItems: 'center',
        fontSize: '13px',
        cursor: 'pointer',
      }}
      onClick={onViewDetail}
    >
      {/* 체크박스 */}
      <div
        style={{ display: 'flex', justifyContent: 'center' }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(e.target.checked)}
          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
        />
      </div>

      {/* 유형 — V73: 휴가 실제 종류 표시 */}
      <div style={{ fontWeight: '500', color: 'var(--color-gray-800)', textAlign: 'center' }}>
        {getTypeDisplayLabel(request.type, request.requestData)}
      </div>

      {/* V69: 사용 단위 — 휴가/휴가취소만 표시 */}
      <div style={{ color: 'var(--color-gray-600)', textAlign: 'center', fontSize: '12px' }}>
        {(request.type === 'leave' || request.type === 'leaveCancel')
          ? getLeaveUnitLabel(request.requestData?.unit)
          : '-'}
      </div>

      {/* 신청자 */}
      <div style={{ color: 'var(--color-gray-700)', textAlign: 'center' }}>
        {request.requesterName || '-'}
      </div>

      {/* 날짜 */}
      <div style={{ color: 'var(--color-gray-700)', textAlign: 'center' }}>{targetDate}</div>

      {/* 요청일시 - V170.1: companyCode 기반 */}
      <div style={{ color: 'var(--color-gray-600)', fontSize: '12px', textAlign: 'center' }}>
        {formatTimestampWithSeconds(request.createdAt, companyCode)}
      </div>

      {/* 승인자 */}
      <div style={{ color: 'var(--color-gray-700)', textAlign: 'center' }}>{request.approverName || '-'}</div>

      {/* 승인일시 - V170.1: companyCode 기반 */}
      <div style={{ color: 'var(--color-gray-600)', fontSize: '12px', textAlign: 'center' }}>
        {approvalTimestamp ? formatTimestampWithSeconds(approvalTimestamp, companyCode) : '-'}
      </div>

      {/* 승인상태 - V55.4: cancelStatus 우선 표시 */}
      <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            background: statusDisplay.bg,
            color: statusDisplay.text,
          }}
        >
          {statusDisplay.label}
        </span>
        {/* V55.1: 취소 승인 대기 뱃지 - V55.4: cancelStatus 사용 시 숨김 */}
        {request.cancelApprovalRequestId && !(request as any).cancelStatus && (
          <span
            style={{
              padding: '2px 6px',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: '500',
              background: 'var(--color-yellow-100)',
              color: 'var(--color-yellow-800)',
            }}
          >
            취소요청중
          </span>
        )}
      </div>

      {/* 내용 */}
      <div
        style={{
          color: 'var(--color-gray-600)',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={content}
      >
        {content}
      </div>
    </div>
  );
}

// ============================================
// 상세 모달 컴포넌트
// ============================================

interface ApprovalDetailModalProps {
  request: ApprovalRequest;
  onClose: () => void;
  companyCode?: string | null; // V170.1
}

function ApprovalDetailModal({ request, onClose, companyCode }: ApprovalDetailModalProps) {
  // V55.4: cancelStatus 우선 표시
  const statusDisplay = getStatusDisplay(request);

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 }}>
              {getTypeDisplayLabel(request.type, request.requestData)}
            </h2>
            <span
              style={{
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500',
                background: statusDisplay.bg,
                color: statusDisplay.text,
              }}
            >
              {statusDisplay.label}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-gray-500)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={{ padding: '20px' }}>
          <InfoRow label="신청자" value={request.requesterName || '-'} />
          <InfoRow label="대상" value={extractTargetDate(request.type, request.requestData)} />
          {/* V70.7: 연장/휴일근무 프로젝트 (공통업무 / 프로젝트명 / 삭제된 프로젝트) */}
          {isProjectSelectableApprovalType(request.type) && (
            <InfoRow label="프로젝트" value={getApprovalProjectLabel(request.requestData, request.projectName)} />
          )}
          {(request.type === 'leave' || request.type === 'leaveCancel') && (
            <InfoRow label="사용 단위" value={
              request.requestData?.unit === 'hourly' && (request.requestData?.workHours || request.requestData?.hours)
                ? `시간단위 (${request.requestData.workHours || request.requestData.hours}시간${request.requestData.startTime && request.requestData.endTime ? ` ${request.requestData.startTime}~${request.requestData.endTime}` : ''})`
                : getLeaveUnitLabel(request.requestData?.unit)
            } />
          )}
          <InfoRow label="승인자" value={request.approverName || '-'} />
          {/* V170.1: companyCode 기반 시간 표시 */}
          <InfoRow label="요청일시" value={formatTimestampWithSeconds(request.createdAt, companyCode)} />
          {request.approvedAt && <InfoRow label="승인일시" value={formatTimestampWithSeconds(request.approvedAt, companyCode)} />}
          {request.rejectedAt && <InfoRow label="반려일시" value={formatTimestampWithSeconds(request.rejectedAt, companyCode)} />}
          {/* V62: 출퇴근 수기조정 상세 — 변경 전/후 표시 */}
          {request.type === 'attendanceManual' && (() => {
            const rd = request.requestData;
            const fmtTime = (v: string | undefined | null): string => {
              if (!v) return '미입력';
              if (typeof v === 'string' && v.includes('T')) {
                return new Date(v).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Seoul' });
              }
              return v;
            };
            const manualType = rd?.attendanceManualType || (rd?.requestedCheckInTime && rd?.requestedCheckOutTime ? 'both' : rd?.requestedCheckInTime ? 'checkIn' : 'checkOut');
            const typeLabel = manualType === 'checkIn' ? '출근' : manualType === 'checkOut' ? '퇴근' : '출퇴근';
            // V66: 익일 퇴근이면 퇴근 시각 앞에 "익일" 라벨
            const isCheckOutNextDay = rd?.checkOutDateOffset === 1;
            const fmtCheckOut = (v: string | undefined | null): string => {
              const base = fmtTime(v);
              if (!v || base === '미입력') return base;
              return isCheckOutNextDay ? `익일 ${base}` : base;
            };
            let adjustmentDisplay: string;
            if (manualType === 'both') {
              adjustmentDisplay = `${fmtTime(rd?.currentCheckInTime)} ~ ${fmtTime(rd?.currentCheckOutTime)}  →  ${fmtTime(rd?.requestedCheckInTime)} ~ ${fmtCheckOut(rd?.requestedCheckOutTime)}`;
            } else if (manualType === 'checkIn') {
              adjustmentDisplay = `${fmtTime(rd?.currentCheckInTime)}  →  ${fmtTime(rd?.requestedCheckInTime)}`;
            } else {
              adjustmentDisplay = `${fmtTime(rd?.currentCheckOutTime)}  →  ${fmtCheckOut(rd?.requestedCheckOutTime)}`;
            }
            return (
              <>
                <InfoRow label="구분" value={typeLabel} />
                <InfoRow label="수기조정" value={adjustmentDisplay} />
              </>
            );
          })()}
          {/* V294.1: 취소 요청 문맥 → 메인 사유란 = 취소사유(B), 신청내용(A)은 보조 행 */}
          {request.cancelRequested && request.cancelReason ? (
            <>
              <InfoRow label="취소사유" value={request.cancelReason} />
              <InfoRow label="신청내용" value={extractContent(request.type, request.requestData)} />
            </>
          ) : (
            <InfoRow label="내용" value={extractContent(request.type, request.requestData)} />
          )}

          {/* V70: 첨부파일 표시 */}
          {Array.isArray(request.requestData?.attachments) && request.requestData.attachments.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ color: 'var(--color-gray-500)', fontSize: '13px', marginBottom: '6px' }}>첨부파일</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {request.requestData.attachments.map((url: string, i: number) => {
                  const isImage = /\.(jpe?g|png|gif|webp)(\?|$)/i.test(url);
                  return (
                    <div key={i}>
                      {isImage && (
                        <img
                          src={url}
                          alt={`첨부 ${i + 1}`}
                          style={{ maxWidth: '240px', maxHeight: '180px', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer' }}
                          onClick={() => window.open(url, '_blank')}
                        />
                      )}
                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px', color: 'var(--color-blue-600)' }}>
                        {isImage ? `이미지 ${i + 1}` : `첨부파일 ${i + 1}`}
                      </a>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {request.comment && (
            <div style={{ background: 'var(--color-gray-50)', padding: '12px', borderRadius: '8px', marginTop: '16px' }}>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginBottom: '4px' }}>
                {request.status === 'rejected' ? '반려 사유' : '코멘트'}
              </div>
              <div style={{ fontSize: '14px', color: 'var(--color-gray-800)' }}>{request.comment}</div>
            </div>
          )}

          {/* V179.1: revoked 상태 표시 */}
          {request.status === 'revoked' && request.revokedAt && (
            <div style={{ background: 'var(--color-purple-50)', padding: '12px', borderRadius: '8px', marginTop: '16px' }}>
              <div style={{ fontSize: '13px', color: 'var(--color-purple-600)', marginBottom: '4px', fontWeight: '500' }}>
                승인 취소됨
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                취소일시: {formatTimestampWithSeconds(request.revokedAt, companyCode)}
              </div>
              {request.revokeReason && (
                <div style={{ fontSize: '14px', color: 'var(--color-gray-800)', marginTop: '8px' }}>
                  사유: {request.revokeReason}
                </div>
              )}
              {request.rollbackResult && (
                <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '8px' }}>
                  대체휴무 조정: {formatCompOffDaysHours(request.rollbackResult.deltaDays)} 차감
                  {request.rollbackResult.markNotFound && ' (마킹 없음)'}
                </div>
              )}
            </div>
          )}

          {/* V179.1: modifyHistory 표시 */}
          {request.modifyHistory && request.modifyHistory.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-gray-700)', marginBottom: '8px' }}>
                수정 이력 ({request.modifyHistory.length}건)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {request.modifyHistory.map((entry, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: 'var(--color-blue-50)',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      fontSize: '13px',
                    }}
                  >
                    <div style={{ color: 'var(--color-gray-600)', marginBottom: '4px' }}>
                      {entry.at?.split('T')[0] || '-'} 수정
                    </div>
                    <div style={{ color: 'var(--color-gray-800)' }}>
                      {entry.oldStartTime}~{entry.oldEndTime} → {entry.newStartTime}~{entry.newEndTime}
                      {entry.newEndDateOffset === 1 && ' (익일)'}
                    </div>
                    <div style={{ color: 'var(--color-gray-600)', marginTop: '4px' }}>
                      {entry.oldApprovedMinutes}분 → {entry.newApprovedMinutes}분
                      {entry.adjustResult && (
                        <span style={{ marginLeft: '8px', color: 'var(--color-blue-600)' }}>
                          (대체휴무 {formatCompOffDelta(entry.adjustResult.daysDelta)})
                        </span>
                      )}
                    </div>
                    {entry.reason && (
                      <div style={{ color: 'var(--color-gray-500)', marginTop: '4px', fontStyle: 'italic' }}>
                        "{entry.reason}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            닫기
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 추가 모달 컴포넌트
// ============================================

interface AddApprovalModalProps {
  onClose: () => void;
  onSuccess: (message?: string) => void;
  onError?: (errorMessage: string) => void;  // V191: 에러 토스트용
  initialCategory?: AddCategory;  // V2: URL param으로 전달된 초기 카테고리
}

// V247: 'holidayWork' → 'weekend' (holiday 카테고리 제거, weekend 직접 사용)
// V66 Phase 4: 'parentalLeave' 제거, 'leaveOfAbsence' 활성화
type AddCategory = 'overtime' | 'weekend' | 'attendanceManual' | 'leave' | 'leaveOfAbsence' | 'leaveOfAbsenceUpdate';

// V188: linkTo 제거 - 휴가신청도 팝업 내부에서 완결
// V66 Phase 4: 휴직 신청 활성화
const ADD_CATEGORIES: { value: AddCategory; label: string; available: boolean }[] = [
  { value: 'overtime', label: '연장근무', available: true },
  { value: 'weekend', label: '휴일근무', available: true },         // V247: holidayWork→weekend
  { value: 'attendanceManual', label: '출퇴근 수기조정', available: true },
  { value: 'leave', label: '휴가신청', available: true },  // V188: 팝업 내부 폼으로 변경
  { value: 'leaveOfAbsence', label: '휴직', available: true },  // V66 Phase 4
  { value: 'leaveOfAbsenceUpdate', label: '휴직 수정 및 연장', available: true },  // V66
];

function AddApprovalModal({ onClose, onSuccess, onError, initialCategory }: AddApprovalModalProps) {
  // V188: navigate 제거 - 모든 승인요청 폼은 팝업 내부에서 완결
  // V2: initialCategory가 있으면 해당 카테고리로 바로 시작
  const [selectedCategory, setSelectedCategory] = useState<AddCategory | null>(initialCategory || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { user } = useCurrentUser();  // V62: CN 연차 선사용 금지 FE 차단

  // V175.1: 근로일정에서 퇴근시간을 OT 시작시간 기본값으로 사용
  const today = new Date().toISOString().split('T')[0];
  const currentMonth = today.substring(0, 7);  // "YYYY-MM"
  const { workSchedule } = useApprovedWorkSchedule(currentMonth);

  // V175.1: 근로일정 퇴근시간을 기본 시작시간으로 사용 (없으면 18:00 fallback)
  const defaultStartTime = workSchedule?.base?.endTime || '18:00';

  // 연장근무 폼 상태
  // V175.1: 시작시간을 근로일정 퇴근시간으로 초기화
  const [overtimeDate, setOvertimeDate] = useState(today);
  const [overtimeStartTime, setOvertimeStartTime] = useState(defaultStartTime);
  const [overtimeEndTime, setOvertimeEndTime] = useState(() => {
    // 시작시간 + 3시간을 종료시간 기본값으로
    const [h, m] = defaultStartTime.split(':').map(Number);
    const endH = (h + 3) % 24;
    return `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });
  const [overtimeEndDateOffset, setOvertimeEndDateOffset] = useState<0 | 1>(0);  // V175: 익일 종료
  const [overtimeReason, setOvertimeReason] = useState('');
  const [overtimeType, setOvertimeType] = useState<'overtime' | 'weekend'>('overtime');  // V247: holiday 제거
  const [overtimeProjectId, setOvertimeProjectId] = useState('NON_PROJECT');  // V121: 프로젝트 선택

  // V64: OT 날짜 기준 workSchedule 조회 (놀금/off-day 판별용)
  const overtimeDateMonth = overtimeDate?.substring(0, 7) || currentMonth;
  const { workSchedule: otWs, monthlyWorkStats: otMonthStats } = useApprovedWorkSchedule(overtimeDateMonth);

  // 출퇴근 수기조정 폼 상태
  // V189: 수기조정 유형 선택 추가 (checkIn/checkOut/both)
  const [attendanceManualType, setAttendanceManualType] = useState<'checkIn' | 'checkOut' | 'both'>('both');
  const [attendanceDate, setAttendanceDate] = useState(today);
  const [attendanceCheckIn, setAttendanceCheckIn] = useState('');
  const [attendanceCheckOut, setAttendanceCheckOut] = useState('');
  // V66: 출퇴근 수기조정 — 퇴근시간이 익일인지 (예: 22:00 출근 → 02:00 퇴근)
  const [attendanceCheckOutNextDay, setAttendanceCheckOutNextDay] = useState(false);
  const [attendanceReason, setAttendanceReason] = useState('');

  // V188: 휴가 신청 폼 상태
  const [leaveTypeCode, setLeaveTypeCode] = useState<LeaveTypeCode>('annual');
  const [leaveStartDate, setLeaveStartDate] = useState(today);
  const [leaveEndDate, setLeaveEndDate] = useState(today);
  const [leaveUnit, setLeaveUnit] = useState<LeaveUnit>('fullDay');
  const [leaveReason, setLeaveReason] = useState('');
  const [uploadedAttachments, setUploadedAttachments] = useState<LeaveAttachment[]>([]);  // V70

  // V66 Phase 4: 휴직 신청 폼 상태
  const [loaType, setLoaType] = useState<LeaveOfAbsenceType>('parental');
  const [loaStartDate, setLoaStartDate] = useState(today);
  const [loaExpectedReturnDate, setLoaExpectedReturnDate] = useState('');
  const [loaReason, setLoaReason] = useState('');

  // V66: 휴직 수정 및 연장 폼 상태
  interface LoaUpdateItem { id: string; type: string; typeName: string; startDate: string; expectedReturnDate: string | null; reason: string; status: string; isCurrent: boolean; }
  const [loaUpdateList, setLoaUpdateList] = useState<LoaUpdateItem[]>([]);
  const [loaUpdateSelectedId, setLoaUpdateSelectedId] = useState('');
  const [loaUpdateType, setLoaUpdateType] = useState<LeaveOfAbsenceType>('parental');
  const [loaUpdateStartDate, setLoaUpdateStartDate] = useState('');
  const [loaUpdateExpectedReturnDate, setLoaUpdateExpectedReturnDate] = useState('');
  const [loaUpdateReason, setLoaUpdateReason] = useState('');
  const [loaUpdateChangeReason, setLoaUpdateChangeReason] = useState('');
  const [loaUpdateLoading, setLoaUpdateLoading] = useState(false);

  // V66: leaveOfAbsenceUpdate 카테고리 진입 시 본인 승인 완료 휴직 리스트 fetch
  React.useEffect(() => {
    if (selectedCategory !== 'leaveOfAbsenceUpdate') return;
    let abort = false;
    (async () => {
      setLoaUpdateLoading(true);
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase/config');
        if (!functions) throw new Error('Firebase Functions 미초기화');
        const fn = httpsCallable<void, { success: boolean; items: LoaUpdateItem[] }>(functions, 'getMyApprovedLeaveOfAbsences');
        const r = await fn();
        if (abort) return;
        setLoaUpdateList(r.data.items || []);
        if (r.data.items && r.data.items.length > 0 && !loaUpdateSelectedId) {
          const first = r.data.items[0];
          setLoaUpdateSelectedId(first.id);
          setLoaUpdateType((first.type as LeaveOfAbsenceType) || 'parental');
          setLoaUpdateStartDate(first.startDate);
          setLoaUpdateExpectedReturnDate(first.expectedReturnDate || '');
          setLoaUpdateReason(first.reason);
        }
      } catch (e: any) {
        console.error('[V66] getMyApprovedLeaveOfAbsences 실패:', e);
      } finally {
        if (!abort) setLoaUpdateLoading(false);
      }
    })();
    return () => { abort = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // V60.6: 대체휴무 시간단위 상태
  const [compOffHours, setCompOffHours] = useState(1);
  const [compOffStartTime, setCompOffStartTime] = useState('09:00');

  // V60.6.2: 대체휴무 시간단위 — 근무시간 SSoT 조회 (휴가 날짜 기준)
  const leaveMonth = leaveStartDate.substring(0, 7);
  const leaveEndMonth = leaveEndDate.substring(0, 7);
  const { workSchedule: leaveWs, monthlyWorkStats: leaveMonthStats } = useApprovedWorkSchedule(leaveMonth);
  const { workSchedule: leaveEndWs, monthlyWorkStats: leaveEndMonthStats } = useApprovedWorkSchedule(leaveEndMonth);
  const leaveScheduleTimes = getScheduleTimesForDate(leaveWs, leaveStartDate);
  const compOffStartOptions = leaveScheduleTimes
    ? generateStartTimeOptions(leaveScheduleTimes.startTime, leaveScheduleTimes.endTime, compOffHours, leaveScheduleTimes.lunchStart, leaveScheduleTimes.lunchEnd)
    : [];

  const { submitOvertimeRequest } = useOvertimeMutations();
  const { requestAttendanceManual } = useAttendanceMutations();
  const { createLeaveRequest } = useLeaveMutations();  // V188
  // V68: 회사별 노출 필터 — user 는 line 1601 useCurrentUser 결과 (같은 컴포넌트 AddApprovalModal)
  const { leaveTypes: leaveTypesRaw } = useLeaveTypes(user?.profile?.companyCode === 'CN' ? 'CN' : 'KR');
  // V69: CN 특수휴가 3종 + personalHealing 자격 필터 (shared SSoT) — dropdown 노출 기준
  const leaveTypes = filterVisibleLeaveTypes(leaveTypesRaw, {
    companyCode: user?.profile?.companyCode,
    currentEmploymentTypeCode: (user?.profile as any)?.currentEmploymentTypeCode,
    gender: (user?.profile as any)?.gender,
  });
  const { balance: leaveBalance } = useMyLeaveBalance();  // V62: CN 연차 선사용 금지 FE 차단
  const { uploadFile, isUploading } = useLeaveUpload();  // V70
  const { projects: assignableProjects, isLoading: isLoadingProjects } = useAssignableProjects(overtimeDate);  // V121: 프로젝트 목록

  // V190: 선택된 날짜의 기존 출퇴근 기록 조회 (정정 모드 판별용)
  const { attendance: existingAttendance } = useTodayAttendance(attendanceDate);
  const isCheckInOverride = !!(attendanceManualType !== 'checkOut' && existingAttendance?.checkInTime);
  const isCheckOutOverride = !!(attendanceManualType !== 'checkIn' && existingAttendance?.checkOutTime);
  const isOverrideMode = isCheckInOverride || isCheckOutOverride;

  // V188: 선택된 휴가유형의 allowedUnits 조회
  const selectedLeaveType = leaveTypes.find((lt) => lt.code === leaveTypeCode);
  // V282-1: 빈 배열도 방어 — 단일 정책 상수 사용
  const allowedUnits: LeaveUnit[] =
    selectedLeaveType?.allowedUnits?.length ? selectedLeaveType.allowedUnits : DEFAULT_ALLOWED_UNITS;

  // V64: 다일자 여부 — 시작일/종료일 문자열(YYYY-MM-DD) 동치 비교
  const isMultiDayLeave = !!(leaveStartDate && leaveEndDate && leaveStartDate !== leaveEndDate);

  // V188: UI용 사용 단위 (halfDay → morningHalf + afternoonHalf 분해)
  // V64: 다일자면 fullDay만 노출
  const leaveUiUnits: LeaveUnit[] = (() => {
    if (isMultiDayLeave) {
      return ['fullDay'];
    }
    const result = new Set<LeaveUnit>();
    for (const unit of allowedUnits) {
      if (unit === 'halfDay') {
        result.add('morningHalf');
        result.add('afternoonHalf');
      } else {
        result.add(unit);
      }
    }
    const sortOrder: LeaveUnit[] = ['fullDay', 'morningHalf', 'afternoonHalf', 'hourly'];
    return sortOrder.filter((unit) => result.has(unit));
  })();

  // V60.6: 대체휴무 시간단위 표시 조건
  const isCompOffHourly = leaveTypeCode === 'compensatory' && leaveUnit === 'hourly';

  // V69: leaveTypes 비동기 로드 후 leaveTypeCode 가 실제 목록에 없으면 첫 번째로 자동 보정.
  //   배경: leaveTypeCode 초기값 'annual' 고정이지만, CN 직원 등 filterVisibleLeaveTypes 결과에 'annual' 이 없을 수 있음.
  //   이 경우 selectedLeaveType=undefined → allowedUnits=DEFAULT(['fullDay']) → leaveUiUnits=['fullDay'] → unit 보정 effect 통과 → 'fullDay' 잔존.
  //   본 effect 가 leaveTypes 로드 직후 leaveTypeCode 를 실제 첫 옵션으로 교정 → selectedLeaveType 식별 → allowedUnits 정상 → unit 보정 발동.
  //   deps 안정화: leaveTypes 는 매 렌더 새 배열 ref 가능 → length 와 첫 code 로 안정 dep 구성.
  const leaveTypesFirstCode = leaveTypes[0]?.code;
  const leaveTypesCount = leaveTypes.length;
  useEffect(() => {
    if (leaveTypesCount === 0) return;
    const exists = leaveTypes.some(lt => lt.code === leaveTypeCode);
    if (!exists && leaveTypesFirstCode) {
      setLeaveTypeCode(leaveTypesFirstCode as LeaveTypeCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveTypesCount, leaveTypesFirstCode, leaveTypeCode]);

  // V188: 휴가유형 변경 시 unit 자동 교정
  // V64: 다일자 전환 시 unit을 fullDay로 강제 보정 + compOff 보조값 초기화
  // V69: deps 안정화 — leaveUiUnits ref 변경 시마다 매번 실행되지만 setState 동일값이면 React 가 skip.
  //   selectedLeaveType 비동기 로드 후 allowedUnits 변화 → leaveUiUnits 변화 → 본 effect 가 다음 cycle 에 보정 실행.
  useEffect(() => {
    if (leaveUiUnits.length === 0) return;
    if (!leaveUiUnits.includes(leaveUnit)) {
      setLeaveUnit(leaveUiUnits[0]);
    }
    // V60.6: 시간단위 해제 시 초기화
    if (leaveUnit !== 'hourly') {
      setCompOffHours(1);
      setCompOffStartTime('09:00');
    }
  }, [leaveTypeCode, leaveUiUnits, leaveUnit]);

  // V60.6.2: 시간수/근무시간 변경 시 시작시간이 허용 범위를 벗어나면 자동 교정
  useEffect(() => {
    if (compOffStartOptions.length > 0 && !compOffStartOptions.includes(compOffStartTime)) {
      setCompOffStartTime(compOffStartOptions[0]);
    }
  }, [compOffHours, compOffStartTime, compOffStartOptions]);

  // V175.1: 근로일정 로드 후 시작시간 업데이트
  useEffect(() => {
    if (workSchedule?.base?.endTime) {
      const newStartTime = workSchedule.base.endTime;
      setOvertimeStartTime(newStartTime);
      // 종료시간도 갱신 (시작시간 + 3시간)
      const [h, m] = newStartTime.split(':').map(Number);
      const endH = (h + 3) % 24;
      setOvertimeEndTime(`${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }, [workSchedule?.base?.endTime]);

  const handleSubmit = async () => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (selectedCategory === 'overtime' || selectedCategory === 'weekend') {
        // V247: 야근 / 휴일근무 (2타입 통합)
        if (!overtimeReason.trim()) {
          throw new Error('사유를 입력해주세요.');
        }
        // V121: 프로젝트 선택 검증
        if (!overtimeProjectId) {
          throw new Error('프로젝트를 선택해주세요.');
        }

        // V247: 카테고리가 곧 OT 타입 (dropdown 제거)
        const type = selectedCategory as 'overtime' | 'weekend';

        // V64: 날짜+타입 FE 사전 검증 (BE가 최종 SSoT)
        {
          const otD = new Date(overtimeDate);
          const otDayOfWeek = otD.getDay();
          const isWeekend = otDayOfWeek === 0 || otDayOfWeek === 6;
          // 공휴일 판별 (monthlyWorkStats.excludedHolidays 활용)
          const otHolidaySet = new Set<string>();
          if (otMonthStats?.excludedHolidays) {
            otMonthStats.excludedHolidays.forEach(h => otHolidaySet.add(h.date));
          }
          const isHolidayDate = otHolidaySet.has(overtimeDate);
          // off-day 판별 (workSchedule.dailyOverrides)
          const otOverride = otWs?.dailyOverrides?.[overtimeDate];
          const isOffDayDate = (otOverride as any)?.startTime === '';
          const isNonWorking = isWeekend || isHolidayDate || isOffDayDate;

          if (type === 'overtime' && isNonWorking) {
            const reasons: string[] = [];
            if (isWeekend) reasons.push(otDayOfWeek === 0 ? '일요일' : '토요일');
            if (isHolidayDate) reasons.push('공휴일');
            if (isOffDayDate) reasons.push('비근무일');
            throw new Error(`${overtimeDate}은(는) ${reasons.join(', ')}이므로 연장근무를 신청할 수 없습니다. 휴일근무로 신청해 주세요.`);
          }
          if (type === 'weekend' && !isNonWorking) {
            throw new Error('휴일근무는 비근무일(토/일/공휴일/놀금)에만 신청 가능합니다.');
          }
        }

        // V175: endDateOffset 추가 (자정 넘김 지원)
        // V121: projectId 추가 (프로젝트 선택)
        await submitOvertimeRequest({
          type,
          date: overtimeDate,
          startTime: overtimeStartTime,
          endTime: overtimeEndTime,
          endDateOffset: overtimeEndDateOffset,
          reason: overtimeReason,
          projectId: overtimeProjectId,  // V121: 프로젝트 ID
        });

        // V247: 야근/휴일근무 성공 메시지
        const typeLabel = selectedCategory === 'overtime' ? '연장근무' : '휴일근무';
        onSuccess(`${typeLabel} 요청이 제출되었습니다.`);
      } else if (selectedCategory === 'attendanceManual') {
        // V189: 출퇴근 수기조정 (유형별 validation)
        if (!attendanceReason.trim()) {
          throw new Error('사유를 입력해주세요.');
        }

        // V189: 유형에 따른 필수값 검증
        if (attendanceManualType === 'checkIn' && !attendanceCheckIn) {
          throw new Error('출근 시간을 입력해주세요.');
        }
        if (attendanceManualType === 'checkOut' && !attendanceCheckOut) {
          throw new Error('퇴근 시간을 입력해주세요.');
        }
        if (attendanceManualType === 'both' && (!attendanceCheckIn || !attendanceCheckOut)) {
          throw new Error('출근 시간과 퇴근 시간을 모두 입력해주세요.');
        }

        // V60.x: "HH:mm" 시간 문자열을 직접 전달 (Date 변환 제거 — UTC 밀림 방지)
        const requestedCheckInTime = (attendanceManualType === 'checkIn' || attendanceManualType === 'both') && attendanceCheckIn
          ? attendanceCheckIn   // "10:00"
          : undefined;
        const requestedCheckOutTime = (attendanceManualType === 'checkOut' || attendanceManualType === 'both') && attendanceCheckOut
          ? attendanceCheckOut  // "19:00"
          : undefined;

        // V66: 퇴근시간이 출근시간보다 빠르면 사용자가 익일 퇴근을 안 켰을 가능성 → 안내
        if (
          (attendanceManualType === 'both' || attendanceManualType === 'checkOut') &&
          requestedCheckOutTime &&
          requestedCheckInTime &&
          !attendanceCheckOutNextDay &&
          requestedCheckOutTime <= requestedCheckInTime
        ) {
          throw new Error('퇴근 시간이 출근 시간보다 이전입니다. 익일 퇴근인 경우 "익일 퇴근" 옵션을 켜주세요.');
        }

        // V54.7: 반환값 체크하여 서버 에러 시 토스트 표시
        // V66: 익일 퇴근 신청 시 checkOutDateOffset=1 함께 전달 (퇴근시간이 있을 때만 의미 있음)
        const result = await requestAttendanceManual({
          date: attendanceDate,
          requestedCheckInTime,
          requestedCheckOutTime,
          ...(attendanceCheckOutNextDay && requestedCheckOutTime ? { checkOutDateOffset: 1 } : {}),
          reason: attendanceReason,
        });

        if (!result.success) {
          throw new Error(result.error || '출퇴근 수정 요청 중 오류가 발생했습니다.');
        }

        // V191: 출퇴근 수기조정 성공 메시지
        onSuccess('출퇴근 수정 요청이 제출되었습니다.');
      } else if (selectedCategory === 'leave') {
        // V188: 휴가 신청

        // V62: CN 연차 잔여일수 초과 신청 FE 사전 차단 (BE가 최종 SSoT)
        if (user?.profile?.companyCode === 'CN' && selectedLeaveType?.deductionType === 'annual' && leaveBalance) {
          const remaining = leaveBalance.annual.remaining ?? 0;
          if (remaining <= 0) {
            alert('사용 가능한 연차가 없습니다.');
            return;
          }
          // V62: 잔여일수 초과 사전 차단 — 주말 제외 영업일 추정
          // V69 FIX (2026-06-02): UI 는 expandAllowedUnitsForUI 가 'halfDay' → 'morningHalf'+'afternoonHalf' 로 분해 노출.
          //   기존엔 legacy 'halfDay' 라벨만 0.5 로 인식해서 morningHalf/afternoonHalf 가 fullDay 영업일 카운트로
          //   fallthrough → estimatedDays=1 → CN 잔여 0.5 신청 시 오탐 차단. 반차 3종 모두 0.5 로 통일.
          if (leaveStartDate && leaveEndDate && leaveUnit) {
            let estimatedDays = 0;
            if (leaveUnit === 'halfDay' || leaveUnit === 'morningHalf' || leaveUnit === 'afternoonHalf') {
              estimatedDays = 0.5;
            } else if (leaveUnit === 'hourly') {
              estimatedDays = (compOffHours || 1) / 8;
            } else {
              // fullDay: 주말 제외 영업일 카운트 (공휴일은 BE에서 최종 판단)
              const start = new Date(leaveStartDate);
              const end = new Date(leaveEndDate);
              for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                const day = d.getDay();
                if (day !== 0 && day !== 6) estimatedDays++;
              }
            }
            if (estimatedDays > remaining) {
              alert(`사용 가능한 연차가 부족합니다. (잔여: ${remaining}일, 예상 신청: ${estimatedDays}일)`);
              return;
            }
          }
        }

        // 유효성 검사
        if (!leaveStartDate || !leaveEndDate) {
          throw new Error('시작일과 종료일을 입력해주세요.');
        }

        // V69: submit guard — selectedUnit 이 현재 휴가유형의 allowedUnits 범위 밖이면 차단.
        //   정상 흐름에선 effect 가 보정하므로 발생 안 함. 비동기 race 또는 사용자 콘솔 조작 대비 마지막 방어막.
        if (leaveUiUnits.length > 0 && !leaveUiUnits.includes(leaveUnit)) {
          throw new Error('선택한 휴가유형에서 사용할 수 없는 사용단위입니다. 사용단위를 다시 선택해주세요.');
        }

        // V67: 종료일이 시작일 이전인지 검증
        if (leaveStartDate > leaveEndDate) {
          throw new Error('종료일은 시작일 이후여야 합니다.');
        }

        // V64: 다일자 신청은 사용 단위가 반드시 '전일'이어야 함 (FE 우회 방어 1차)
        if (leaveStartDate !== leaveEndDate && leaveUnit !== 'fullDay') {
          throw new Error('2일 이상 휴가는 사용 단위를 "전일"로만 신청할 수 있습니다. 반차/시간단위는 단일 날짜에서만 사용 가능합니다.');
        }

        // V64: 비근무일(주말/공휴일/놀금) 휴가 신청 FE 사전 차단
        {
          // 공휴일 Set 구성 (시작월 + 종료월)
          const holidaySet = new Set<string>();
          if (leaveMonthStats?.excludedHolidays) {
            leaveMonthStats.excludedHolidays.forEach(h => holidaySet.add(h.date));
          }
          if (leaveEndMonth !== leaveMonth && leaveEndMonthStats?.excludedHolidays) {
            leaveEndMonthStats.excludedHolidays.forEach(h => holidaySet.add(h.date));
          }

          // workSchedule off-day Set 구성
          const offDaySet = new Set<string>();
          const addOffDays = (ws: typeof leaveWs) => {
            if (!ws?.dailyOverrides) return;
            Object.entries(ws.dailyOverrides).forEach(([dateKey, ov]: [string, any]) => {
              if (ov?.startTime === '') offDaySet.add(dateKey);
            });
          };
          addOffDays(leaveWs);
          if (leaveEndMonth !== leaveMonth) addOffDays(leaveEndWs);

          const isNonWorkingDate = (dateStr: string): string | null => {
            const d = new Date(dateStr);
            const day = d.getDay();
            if (day === 0) return '일요일';
            if (day === 6) return '토요일';
            if (holidaySet.has(dateStr)) return '공휴일';
            if (offDaySet.has(dateStr)) return '비근무일';
            return null;
          };

          if (leaveStartDate === leaveEndDate) {
            // 단일일 신청: 비근무일이면 즉시 차단
            const reason = isNonWorkingDate(leaveStartDate);
            if (reason) {
              throw new Error(`${leaveStartDate}은(는) ${reason}이므로 휴가를 신청할 수 없습니다.`);
            }
          } else {
            // 다일자 신청: 전체가 비근무일이면 차단
            let hasWorkingDay = false;
            const cur = new Date(leaveStartDate);
            const endD = new Date(leaveEndDate);
            while (cur <= endD) {
              const ds = cur.toISOString().split('T')[0];
              if (!isNonWorkingDate(ds)) {
                hasWorkingDay = true;
                break;
              }
              cur.setDate(cur.getDate() + 1);
            }
            if (!hasWorkingDay) {
              throw new Error('선택한 기간 내에 근무일이 없습니다. 주말, 공휴일, 비근무일만 포함된 기간에는 휴가를 신청할 수 없습니다.');
            }
          }
        }

        // V33.6: reason 정규화 (undefined/빈문자열 방지)
        const normalizedMetadata: Record<string, any> = {};
        const trimmedReason = leaveReason.trim();
        if (trimmedReason !== '') {
          normalizedMetadata.reason = trimmedReason;
        }

        // V70: 첨부파일 URL 추가
        if (uploadedAttachments.length > 0) {
          normalizedMetadata.attachments = uploadedAttachments.map(a => a.url);
        }

        // V60.6: 대체휴무 시간단위 — 시간 정보 metadata에 추가
        if (isCompOffHourly) {
          // V60.6.2: 근무시간 SSoT 기반 범위 검증
          if (!leaveScheduleTimes) {
            alert('근로일정을 조회할 수 없습니다. 날짜를 확인해주세요.');
            return;
          }
          // V280.1: 점심시간 정보 필수 — 누락 시 차단
          if (!leaveScheduleTimes.lunchStart || !leaveScheduleTimes.lunchEnd) {
            alert('근로일정에 점심시간 정보가 없습니다. 근로일정을 확인하거나 HR에 문의해주세요.');
            return;
          }
          // V62: 점심시간 스킵 기반 종료시간 계산
          const endTime = calcEndTimeSkippingBreaks(compOffStartTime, compOffHours, leaveScheduleTimes.lunchStart, leaveScheduleTimes.lunchEnd);
          const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
          const endMin = toMin(endTime);
          const schedEndMin = toMin(leaveScheduleTimes.endTime);
          if (endMin > schedEndMin) {
            alert(`종료시간(${endTime})이 근무시간(${leaveScheduleTimes.endTime})을 초과합니다. 시작시간 또는 사용시간을 조정해주세요.`);
            return;
          }
          // V62: metadata.hours = clock span (BE validation: endMin - startMin === hours * 60)
          const clockSpanHours = (endMin - toMin(compOffStartTime)) / 60;
          normalizedMetadata.hours = clockSpanHours;
          // V64: 실제 사용시간(점심 제외) 별도 저장 — 상세 모달 표시용
          normalizedMetadata.workHours = compOffHours;
          normalizedMetadata.startTime = compOffStartTime;
          normalizedMetadata.endTime = endTime;
        }

        const requestData: LeaveRequestInput = {
          leaveTypeCode,
          startDate: leaveStartDate,
          endDate: leaveEndDate,
          unit: leaveUnit,
          metadata: normalizedMetadata,
        };

        await createLeaveRequest(requestData);
        setUploadedAttachments([]);  // V70: 첨부 초기화
        setCompOffHours(1);  // V60.6: 시간 초기화
        setCompOffStartTime('09:00');

        // V191: 휴가 성공 메시지
        onSuccess('휴가 신청이 제출되었습니다.');
      } else if (selectedCategory === 'leaveOfAbsence') {
        // V66 Phase 4: 본인 휴직 신청
        if (!loaStartDate) {
          throw new Error('시작일을 입력해주세요.');
        }
        if (!loaExpectedReturnDate) {
          throw new Error('종료일을 입력해주세요.');
        }
        if (loaStartDate > loaExpectedReturnDate) {
          throw new Error('종료일은 시작일 이후여야 합니다.');
        }
        const maxEnd = calcMaxExpectedReturnDate(loaType, loaStartDate);
        if (maxEnd && loaExpectedReturnDate > maxEnd) {
          throw new Error(
            `${LOA_TYPE_LABELS[loaType]}의 최대 기간(${LOA_TYPE_MAX_LABELS[loaType]})을 초과했습니다. 최대 가능 종료일: ${maxEnd}`
          );
        }
        await createLeaveOfAbsenceRequest({
          type: loaType,
          startDate: loaStartDate,
          expectedReturnDate: loaExpectedReturnDate,
          reason: loaReason.trim() || undefined,
        });
        onSuccess('휴직 신청이 제출되었습니다. 조직장 → 인사팀 순으로 승인됩니다.');
      } else if (selectedCategory === 'leaveOfAbsenceUpdate') {
        // V66: 휴직 수정 및 연장
        if (!loaUpdateSelectedId) throw new Error('수정할 휴직을 선택해주세요.');
        if (!loaUpdateStartDate || !loaUpdateExpectedReturnDate) throw new Error('시작일/종료일을 입력해주세요.');
        if (loaUpdateStartDate > loaUpdateExpectedReturnDate) throw new Error('종료일은 시작일 이후여야 합니다.');
        // V66: 최대 사용기한 사전 검증 (BE validateLoaInputs 와 동일 정책)
        const maxEndU = calcMaxExpectedReturnDate(loaUpdateType, loaUpdateStartDate);
        if (maxEndU && loaUpdateExpectedReturnDate > maxEndU) {
          throw new Error(
            `${LOA_TYPE_LABELS[loaUpdateType]}의 최대 기간(${LOA_TYPE_MAX_LABELS[loaUpdateType]})을 초과했습니다. 최대 가능 종료일: ${maxEndU}`,
          );
        }
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase/config');
        if (!functions) throw new Error('Firebase Functions 미초기화');
        const fn = httpsCallable<{
          leaveOfAbsenceId: string; type: LeaveOfAbsenceType; startDate: string; expectedReturnDate: string;
          reason?: string; changeReason?: string;
        }, { success: boolean; approvalRequestId: string }>(functions, 'createLeaveOfAbsenceUpdateRequest');
        await fn({
          leaveOfAbsenceId: loaUpdateSelectedId,
          type: loaUpdateType,
          startDate: loaUpdateStartDate,
          expectedReturnDate: loaUpdateExpectedReturnDate,
          reason: loaUpdateReason.trim() || undefined,
          changeReason: loaUpdateChangeReason.trim() || undefined,
        });
        onSuccess('휴직 수정 신청이 제출되었습니다. 조직장 → 인사팀 순으로 승인됩니다.');
      } else {
        // 미구현 기능
        throw new Error('해당 기능은 준비중입니다.');
      }
    } catch (err: any) {
      const errorMessage = err.message || '제출 중 오류가 발생했습니다.';
      setSubmitError(errorMessage);
      // V191: 부모 컴포넌트에도 에러 전달 (모달 닫힌 후 토스트용)
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 }}>
            승인요청 추가
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-gray-500)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 본문 */}
        <div style={{ padding: '20px' }}>
          {/* 카테고리 선택 */}
          {/* V188: linkTo 제거 - 모든 카테고리는 팝업 내부에서 폼 렌더링 */}
          {!selectedCategory && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {ADD_CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => {
                    if (!cat.available) return;
                    setSelectedCategory(cat.value);
                  }}
                  disabled={!cat.available}
                  style={{
                    padding: '16px',
                    background: cat.available ? 'var(--color-gray-50)' : 'var(--color-gray-100)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: '8px',
                    cursor: cat.available ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: cat.available ? 'var(--color-gray-800)' : 'var(--color-gray-400)',
                    textAlign: 'center',
                  }}
                >
                  {cat.label}
                  {!cat.available && (
                    <span style={{ display: 'block', fontSize: '11px', color: 'var(--color-gray-400)', marginTop: '4px' }}>
                      준비중
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* V247: 야근 / 휴일근무 폼 */}
          {(selectedCategory === 'overtime' || selectedCategory === 'weekend') && (
            <div>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--color-primary)',
                  marginBottom: '16px',
                  padding: 0,
                }}
              >
                ← 뒤로
              </button>

              {/* V247: 카테고리가 곧 타입 — 드롭다운 제거 */}
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                {selectedCategory === 'overtime' ? '연장근무' : '휴일근무'}
              </h3>

              <FormField label="날짜">
                <input
                  type="date"
                  value={overtimeDate}
                  onChange={(e) => setOvertimeDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                />
              </FormField>

              {/* V121: 프로젝트 선택 */}
              <FormField label="프로젝트">
                <select
                  value={overtimeProjectId}
                  onChange={(e) => setOvertimeProjectId(e.target.value)}
                  disabled={isLoadingProjects}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                >
                  {isLoadingProjects ? (
                    <option value="">로딩 중...</option>
                  ) : assignableProjects.length === 0 ? (
                    <option value="NON_PROJECT">공통업무 (비프로젝트)</option>
                  ) : (
                    assignableProjects.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectName}
                      </option>
                    ))
                  )}
                </select>
                {assignableProjects.length > 1 && (
                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                    {overtimeDate} 기준 투입 프로젝트 {assignableProjects.length - 1}개
                  </div>
                )}
                {/* V127: 정책 A 안내 - NON_PROJECT 선택 시 경고 */}
                {overtimeProjectId === 'NON_PROJECT' && assignableProjects.length > 1 && (
                  <div style={{ fontSize: '12px', color: 'var(--color-yellow-600)', marginTop: '4px' }}>
                    ⚠ 프로젝트 미지정 시 MM현황 실투입MD에 반영되지 않습니다
                  </div>
                )}
              </FormField>

              {/* V175: 시작/종료 시간 + 익일 토글 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <FormField label="시작 시간">
                  <TimeStepperInput
                    value={overtimeStartTime}
                    onChange={setOvertimeStartTime}
                  />
                </FormField>

                <FormField label={overtimeEndDateOffset === 1 ? '종료 시간 (익일)' : '종료 시간'}>
                  <TimeStepperInput
                    value={overtimeEndTime}
                    onChange={setOvertimeEndTime}
                  />
                </FormField>
              </div>

              {/* V175: 익일 종료 토글 */}
              <div style={{ marginTop: '12px', marginBottom: '12px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'var(--color-gray-700)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={overtimeEndDateOffset === 1}
                    onChange={(e) => setOvertimeEndDateOffset(e.target.checked ? 1 : 0)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span>종료 시간이 익일 (자정 넘김)</span>
                  {overtimeEndDateOffset === 1 && (
                    <span style={{ fontSize: '12px', color: 'var(--color-primary)', fontWeight: '500' }}>
                      예: {overtimeDate.slice(5)} {overtimeStartTime} ~ {getNextDate(overtimeDate).slice(5)} {overtimeEndTime}
                    </span>
                  )}
                </label>
              </div>

              <FormField label="사유">
                <textarea
                  value={overtimeReason}
                  onChange={(e) => setOvertimeReason(e.target.value)}
                  placeholder="연장근무 사유를 입력하세요"
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', resize: 'vertical' }}
                />
              </FormField>
            </div>
          )}

          {/* V189: 출퇴근 수기조정 폼 (유형 선택 추가) */}
          {selectedCategory === 'attendanceManual' && (
            <div>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--color-primary)',
                  marginBottom: '16px',
                  padding: 0,
                }}
              >
                ← 뒤로
              </button>

              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>출퇴근 수기조정</h3>

              {/* V190: 정정 모드 안내 */}
              {isOverrideMode && (
                <div style={{
                  padding: '12px',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  background: 'var(--color-yellow-50)',
                  border: '1px solid var(--color-yellow-300)',
                }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-yellow-800)', marginBottom: '4px' }}>
                    ⚠️ 정정 모드
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-yellow-700)' }}>
                    {isCheckInOverride && isCheckOutOverride
                      ? '해당 날짜에 이미 출퇴근 기록이 있습니다. 승인 시 기존 시간이 덮어씌워집니다.'
                      : isCheckInOverride
                        ? '해당 날짜에 이미 출근 기록이 있습니다. 승인 시 기존 출근 시간이 덮어씌워집니다.'
                        : '해당 날짜에 이미 퇴근 기록이 있습니다. 승인 시 기존 퇴근 시간이 덮어씌워집니다.'}
                  </div>
                </div>
              )}

              {/* V189: 수기조정 유형 선택 */}
              <FormField label="수기조정 유형">
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { value: 'checkIn' as const, label: '출근' },
                    { value: 'checkOut' as const, label: '퇴근' },
                    { value: 'both' as const, label: '출퇴근' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setAttendanceManualType(opt.value);
                        // 유형 변경 시 해당하지 않는 필드 초기화
                        if (opt.value === 'checkIn') setAttendanceCheckOut('');
                        if (opt.value === 'checkOut') setAttendanceCheckIn('');
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 12px',
                        borderRadius: '6px',
                        border: attendanceManualType === opt.value
                          ? '2px solid var(--color-primary)'
                          : '1px solid var(--color-gray-300)',
                        background: attendanceManualType === opt.value
                          ? 'var(--color-primary-50)'
                          : 'white',
                        color: attendanceManualType === opt.value
                          ? 'var(--color-primary)'
                          : 'var(--color-gray-700)',
                        fontWeight: attendanceManualType === opt.value ? '600' : '400',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </FormField>

              <FormField label="날짜">
                <input
                  type="date"
                  value={attendanceDate}
                  onChange={(e) => setAttendanceDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                />
              </FormField>

              {/* V189: 유형에 따른 조건부 시간 필드 / V66: 익일 퇴근 옵션 */}
              {attendanceManualType === 'both' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <FormField label="출근 시간 *">
                    <input
                      type="time"
                      value={attendanceCheckIn}
                      onChange={(e) => setAttendanceCheckIn(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                    />
                  </FormField>
                  <FormField label="퇴근 시간 *">
                    <input
                      type="time"
                      value={attendanceCheckOut}
                      onChange={(e) => setAttendanceCheckOut(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                    />
                  </FormField>
                </div>
              ) : attendanceManualType === 'checkIn' ? (
                <FormField label="출근 시간 *">
                  <input
                    type="time"
                    value={attendanceCheckIn}
                    onChange={(e) => setAttendanceCheckIn(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                  />
                </FormField>
              ) : (
                <FormField label="퇴근 시간 *">
                  <input
                    type="time"
                    value={attendanceCheckOut}
                    onChange={(e) => setAttendanceCheckOut(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                  />
                </FormField>
              )}

              {/* V66: 익일 퇴근 체크박스 (퇴근시간이 포함된 유형에서만 노출) */}
              {(attendanceManualType === 'both' || attendanceManualType === 'checkOut') && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    marginTop: '4px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={attendanceCheckOutNextDay}
                    onChange={(e) => setAttendanceCheckOutNextDay(e.target.checked)}
                    style={{ width: '16px', height: '16px' }}
                  />
                  <span style={{ fontSize: '13px', color: 'var(--color-gray-700)' }}>
                    익일 퇴근 (예: 22:00 출근 후 다음날 02:00 퇴근)
                  </span>
                </label>
              )}

              <FormField label="사유 *">
                <textarea
                  value={attendanceReason}
                  onChange={(e) => setAttendanceReason(e.target.value)}
                  placeholder="수정 사유를 입력하세요 (필수)"
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', resize: 'vertical' }}
                />
              </FormField>
            </div>
          )}

          {/* V188: 휴가 신청 폼 */}
          {selectedCategory === 'leave' && (
            <div>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--color-primary)',
                  marginBottom: '16px',
                  padding: 0,
                }}
              >
                ← 뒤로
              </button>

              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>휴가 신청</h3>

              {/* V62: CN 연차 선사용 금지 안내 배너 — V68: 정책 라벨 미노출, 잔액 0 이하일 때만 잔액 부족 경고 유지 */}
              {user?.profile?.companyCode === 'CN' && selectedLeaveType?.deductionType === 'annual' && leaveBalance && leaveBalance.annual.remaining <= 0 && (
                <div style={{
                  padding: '10px 12px', marginBottom: '16px', borderRadius: '6px', fontSize: '13px', lineHeight: '1.5',
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                }}>
                  {`사용 가능한 연차가 없습니다. (잔여: ${leaveBalance.annual.remaining}일)`}
                </div>
              )}

              <FormField label="휴가 유형">
                <select
                  value={leaveTypeCode}
                  onChange={(e) => setLeaveTypeCode(e.target.value as LeaveTypeCode)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                >
                  {leaveTypes.map((lt) => (
                    <option key={lt.code} value={lt.code}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </FormField>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <FormField label="시작일">
                  <input
                    type="date"
                    value={leaveStartDate}
                    onChange={(e) => {
                      // V66 (sync): 시작일 변경 시 종료일을 시작일과 동일하게 자동 동기화.
                      //   사용자가 이후 종료일을 직접 변경하면 그대로 허용 (endDate input 은 별도 onChange).
                      const v = e.target.value;
                      setLeaveStartDate(v);
                      setLeaveEndDate(v);
                    }}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                  />
                </FormField>

                <FormField label="종료일">
                  <input
                    type="date"
                    value={leaveEndDate}
                    onChange={(e) => setLeaveEndDate(e.target.value)}
                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                  />
                </FormField>
              </div>

              <FormField label="사용 단위">
                <select
                  value={leaveUnit}
                  onChange={(e) => setLeaveUnit(e.target.value as LeaveUnit)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                >
                  {leaveUiUnits.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit === 'fullDay' && '전일 (1일)'}
                      {unit === 'morningHalf' && '오전반차 (0.5일)'}
                      {unit === 'afternoonHalf' && '오후반차 (0.5일)'}
                      {unit === 'hourly' && '시간단위'}
                    </option>
                  ))}
                </select>
                {/* V60.6: 대체휴무 시간단위 — 시간수/시작시간/종료시간 선택 */}
                {isCompOffHourly && (
                  <div style={{
                    marginTop: '10px', padding: '12px',
                    background: 'var(--color-blue-50, #eff6ff)', borderRadius: '6px',
                    border: '1px solid var(--color-blue-200, #bfdbfe)',
                  }}>
                    {/* V60.6.2: 근무시간 범위 안내 */}
                    {leaveScheduleTimes && (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: 'var(--color-gray-600)' }}>
                        근무시간: {leaveScheduleTimes.startTime} ~ {leaveScheduleTimes.endTime}
                      </div>
                    )}
                    {!leaveScheduleTimes && (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: 'var(--color-red-600)' }}>
                        해당 날짜의 근로일정이 없습니다. 먼저 근로일정을 신청/승인 받으세요.
                      </div>
                    )}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>사용 시간</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button type="button" onClick={() => setCompOffHours(Math.max(1, compOffHours - 1))} disabled={compOffHours <= 1}
                          style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', background: compOffHours <= 1 ? 'var(--color-gray-100)' : 'white', cursor: compOffHours <= 1 ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: 'bold' }}>−</button>
                        <span style={{ fontSize: '16px', fontWeight: '600', minWidth: '60px', textAlign: 'center' }}>{compOffHours}시간</span>
                        <button type="button" onClick={() => setCompOffHours(Math.min(8, compOffHours + 1))} disabled={compOffHours >= 8}
                          style={{ width: '32px', height: '32px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', background: compOffHours >= 8 ? 'var(--color-gray-100)' : 'white', cursor: compOffHours >= 8 ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: 'bold' }}>+</button>
                        <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>({compOffHours}시간 차감)</span>
                      </div>
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>시작시간</label>
                      <select value={compOffStartTime} onChange={(e) => setCompOffStartTime(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid var(--color-gray-300)', borderRadius: '4px' }}>
                        {compOffStartOptions.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    {/* V280.1: lunch 정보가 있을 때만 정확한 계산 표시, 없으면 경고 */}
                    {leaveScheduleTimes?.lunchStart && leaveScheduleTimes?.lunchEnd ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: 'white', borderRadius: '4px', border: '1px solid var(--color-gray-200)' }}>
                        <span style={{ fontSize: '13px' }}>
                          {compOffStartTime} ~ {calcEndTimeSkippingBreaks(compOffStartTime, compOffHours, leaveScheduleTimes.lunchStart, leaveScheduleTimes.lunchEnd)}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>(실 {compOffHours}시간, 점심 {leaveScheduleTimes.lunchStart}~{leaveScheduleTimes.lunchEnd} 제외)</span>
                      </div>
                    ) : (
                      <div style={{ padding: '8px 10px', background: 'var(--color-red-50, #fef2f2)', borderRadius: '4px', border: '1px solid var(--color-red-200, #fecaca)', fontSize: '12px', color: 'var(--color-red-700, #b91c1c)' }}>
                        근로일정에 점심시간 정보가 없어 종료시간을 계산할 수 없습니다. 근로일정을 확인해주세요.
                      </div>
                    )}
                  </div>
                )}
              </FormField>

              <FormField label={selectedLeaveType?.requiresReason ? '사유 (필수)' : '사유 (선택)'}>
                <textarea
                  value={leaveReason}
                  onChange={(e) => setLeaveReason(e.target.value)}
                  placeholder="사유를 입력하세요"
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', resize: 'vertical' }}
                />
              </FormField>

              {/* V70: 첨부파일 업로드 */}
              <FormField label={`첨부파일 ${selectedLeaveType?.requiresAttachment ? '(필수)' : '(선택)'}`}>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const attachment = await uploadFile(file);
                      setUploadedAttachments(prev => [...prev, attachment]);
                    } catch (err: any) {
                      alert(`파일 업로드 실패: ${err.message}`);
                    }
                    e.target.value = '';
                  }}
                  disabled={isUploading}
                  style={{ fontSize: '14px' }}
                />
                {isUploading && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-blue-600)' }}>
                    업로드 중...
                  </div>
                )}
                {uploadedAttachments.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    {uploadedAttachments.map((att) => (
                      <div key={att.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 8px',
                        background: 'var(--color-gray-50)',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        fontSize: '13px',
                      }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.name} ({(att.size / 1024).toFixed(1)}KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => setUploadedAttachments(prev => prev.filter(a => a.id !== att.id))}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-red-500)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            padding: '2px 6px',
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {selectedLeaveType?.requiresAttachment && uploadedAttachments.length === 0 && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-red-500)' }}>
                    * 이 휴가 유형은 첨부파일이 필수입니다.
                  </div>
                )}
              </FormField>
            </div>
          )}

          {/* V66 Phase 4: 휴직 신청 폼 */}
          {selectedCategory === 'leaveOfAbsence' && (
            <div>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: 'var(--color-primary)',
                  marginBottom: '16px',
                  padding: 0,
                }}
              >
                ← 뒤로
              </button>

              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                휴직 신청
              </h3>

              <div style={{
                background: 'var(--color-gray-50)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                padding: '10px 12px',
                marginBottom: '16px',
                fontSize: '12px',
                color: 'var(--color-gray-700)',
                lineHeight: 1.5,
              }}>
                휴직은 <b>조직장 승인 → 인사팀 최종 승인</b> 2단계로 처리됩니다.<br/>
                인사팀 최종 승인 시점에 휴직이 확정되며, 휴직 시작일이 도래하면 자동으로 휴직 상태로 전환됩니다.
              </div>

              <FormField label="휴직 종류">
                <select
                  value={loaType}
                  onChange={(e) => {
                    const next = e.target.value as LeaveOfAbsenceType;
                    setLoaType(next);
                    // 종류 변경 시 복귀예정일이 최대치를 초과하면 초기화
                    if (loaStartDate) {
                      const maxEnd = calcMaxExpectedReturnDate(next, loaStartDate);
                      if (loaExpectedReturnDate && maxEnd && loaExpectedReturnDate > maxEnd) {
                        setLoaExpectedReturnDate('');
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                >
                  <option value="parental">{LOA_TYPE_LABELS.parental} (최대 {LOA_TYPE_MAX_LABELS.parental})</option>
                  <option value="familyCare">{LOA_TYPE_LABELS.familyCare} (최대 {LOA_TYPE_MAX_LABELS.familyCare})</option>
                  <option value="general">{LOA_TYPE_LABELS.general} (최대 {LOA_TYPE_MAX_LABELS.general})</option>
                  <option value="medical">{LOA_TYPE_LABELS.medical} (최대 {LOA_TYPE_MAX_LABELS.medical})</option>
                </select>
              </FormField>

              <FormField label="시작일">
                <input
                  type="date"
                  value={loaStartDate}
                  onChange={(e) => {
                    const next = e.target.value;
                    setLoaStartDate(next);
                    // 시작일 변경 시 복귀예정일이 최대치를 초과하면 초기화
                    if (next) {
                      const maxEnd = calcMaxExpectedReturnDate(loaType, next);
                      if (loaExpectedReturnDate && maxEnd && loaExpectedReturnDate > maxEnd) {
                        setLoaExpectedReturnDate('');
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                />
              </FormField>

              <FormField label="종료일">
                <input
                  type="date"
                  value={loaExpectedReturnDate}
                  min={loaStartDate || undefined}
                  max={loaStartDate ? calcMaxExpectedReturnDate(loaType, loaStartDate) : undefined}
                  onChange={(e) => setLoaExpectedReturnDate(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                />
                {loaStartDate && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                    최대 종료일: {calcMaxExpectedReturnDate(loaType, loaStartDate) || '-'}
                  </div>
                )}
              </FormField>

              <FormField label="사유 (선택)">
                <textarea
                  value={loaReason}
                  onChange={(e) => setLoaReason(e.target.value)}
                  placeholder="휴직 사유를 입력해주세요."
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', resize: 'vertical', fontFamily: 'inherit' }}
                />
              </FormField>
            </div>
          )}

          {/* V66: 휴직 수정 및 연장 폼 */}
          {selectedCategory === 'leaveOfAbsenceUpdate' && (
            <div>
              <button
                onClick={() => setSelectedCategory(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', color: 'var(--color-primary)', marginBottom: '16px', padding: 0 }}
              >
                ← 뒤로
              </button>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>휴직 수정 및 연장</h3>
              <div style={{
                background: 'var(--color-gray-50)', border: '1px solid var(--color-gray-200)', borderRadius: '6px',
                padding: '10px 12px', marginBottom: '16px', fontSize: '12px', color: 'var(--color-gray-700)', lineHeight: 1.5,
              }}>
                승인 완료된 본인 휴직의 시작일/종료일/유형/사유를 수정 신청합니다.<br/>
                <b>조직장 승인 → 인사팀 최종 승인</b> 후에 반영됩니다.
              </div>

              {loaUpdateLoading && <div style={{ fontSize: 13, color: '#6b7280' }}>로딩 중...</div>}
              {!loaUpdateLoading && loaUpdateList.length === 0 && (
                <div style={{ padding: 20, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#854d0e' }}>
                  수정 가능한 승인 완료 휴직이 없습니다. (현재 또는 예정된 휴직만 표시됩니다.)
                </div>
              )}
              {!loaUpdateLoading && loaUpdateList.length > 0 && (
                <>
                  <FormField label="대상 휴직">
                    <select
                      value={loaUpdateSelectedId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setLoaUpdateSelectedId(id);
                        const found = loaUpdateList.find((x) => x.id === id);
                        if (found) {
                          setLoaUpdateType((found.type as LeaveOfAbsenceType) || 'parental');
                          setLoaUpdateStartDate(found.startDate);
                          setLoaUpdateExpectedReturnDate(found.expectedReturnDate || '');
                          setLoaUpdateReason(found.reason);
                        }
                      }}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                    >
                      {loaUpdateList.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.typeName} · {x.startDate} ~ {x.expectedReturnDate || '미정'}
                        </option>
                      ))}
                    </select>
                  </FormField>
                  <FormField label="휴직 종류">
                    <select
                      value={loaUpdateType}
                      onChange={(e) => {
                        const next = e.target.value as LeaveOfAbsenceType;
                        setLoaUpdateType(next);
                        // V66: 종류 변경 시 복귀예정일이 새 max 초과면 초기화 (신규 신청 폼과 동일 정책)
                        if (loaUpdateStartDate) {
                          const maxEnd = calcMaxExpectedReturnDate(next, loaUpdateStartDate);
                          if (loaUpdateExpectedReturnDate && maxEnd && loaUpdateExpectedReturnDate > maxEnd) {
                            setLoaUpdateExpectedReturnDate('');
                          }
                        }
                      }}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                    >
                      <option value="parental">{LOA_TYPE_LABELS.parental} (최대 {LOA_TYPE_MAX_LABELS.parental})</option>
                      <option value="familyCare">{LOA_TYPE_LABELS.familyCare} (최대 {LOA_TYPE_MAX_LABELS.familyCare})</option>
                      <option value="general">{LOA_TYPE_LABELS.general} (최대 {LOA_TYPE_MAX_LABELS.general})</option>
                      <option value="medical">{LOA_TYPE_LABELS.medical} (최대 {LOA_TYPE_MAX_LABELS.medical})</option>
                    </select>
                  </FormField>
                  <FormField label="시작일">
                    <input
                      type="date"
                      value={loaUpdateStartDate}
                      onChange={(e) => {
                        const next = e.target.value;
                        setLoaUpdateStartDate(next);
                        // V66: 시작일 변경 시 복귀예정일이 새 max 초과면 초기화
                        if (next) {
                          const maxEnd = calcMaxExpectedReturnDate(loaUpdateType, next);
                          if (loaUpdateExpectedReturnDate && maxEnd && loaUpdateExpectedReturnDate > maxEnd) {
                            setLoaUpdateExpectedReturnDate('');
                          }
                        }
                      }}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                    />
                  </FormField>
                  <FormField label="종료일">
                    <input
                      type="date"
                      value={loaUpdateExpectedReturnDate}
                      min={loaUpdateStartDate || undefined}
                      max={loaUpdateStartDate ? calcMaxExpectedReturnDate(loaUpdateType, loaUpdateStartDate) : undefined}
                      onChange={(e) => setLoaUpdateExpectedReturnDate(e.target.value)}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)' }}
                    />
                    {loaUpdateStartDate && (
                      <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-gray-500)' }}>
                        최대 종료일: {calcMaxExpectedReturnDate(loaUpdateType, loaUpdateStartDate) || '-'}
                        {' '}(최대 사용기한 {LOA_TYPE_MAX_LABELS[loaUpdateType]})
                      </div>
                    )}
                  </FormField>
                  <FormField label="사유 (선택)">
                    <textarea
                      value={loaUpdateReason}
                      onChange={(e) => setLoaUpdateReason(e.target.value)}
                      rows={2}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </FormField>
                  <FormField label="변경 사유 (선택)">
                    <textarea
                      value={loaUpdateChangeReason}
                      onChange={(e) => setLoaUpdateChangeReason(e.target.value)}
                      placeholder="기간/유형 변경 사유를 입력해주세요."
                      rows={2}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-gray-300)', resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </FormField>
                </>
              )}
            </div>
          )}

          {/* 에러 메시지 */}
          {submitError && (
            <div style={{ background: 'var(--color-red-50)', color: 'var(--color-red-600)', padding: '12px', borderRadius: '6px', marginTop: '16px', fontSize: '14px' }}>
              {submitError}
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            취소
          </button>

          {selectedCategory && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || isUploading || (selectedCategory === 'leave' && !!selectedLeaveType?.requiresAttachment && uploadedAttachments.length === 0)}
              style={{
                padding: '10px 20px',
                background: (isSubmitting || isUploading || (selectedCategory === 'leave' && !!selectedLeaveType?.requiresAttachment && uploadedAttachments.length === 0)) ? 'var(--color-gray-300)' : 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: (isSubmitting || isUploading) ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? '제출 중...' : isUploading ? '업로드 중...' : '제출'}
            </button>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 삭제 확인 모달
// ============================================

interface DeleteConfirmModalProps {
  count: number;
  isDeleting: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmModal({ count, isDeleting, error, onConfirm, onCancel }: DeleteConfirmModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
      onClick={onCancel}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '100%',
          maxWidth: '400px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* V193: 삭제 → 취소 용어 변경 (soft delete) */}
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--color-gray-800)' }}>
          요청 취소 확인
        </h3>

        <p style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '16px' }}>
          선택한 {count}건의 요청을 취소하시겠습니까?
          <br />
          <span style={{ color: 'var(--color-gray-500)' }}>
            ※ 취소된 요청은 목록에서 사라지며, 승인자에게도 표시되지 않습니다.
          </span>
        </p>

        {error && (
          <div style={{ background: 'var(--color-red-50)', color: 'var(--color-red-600)', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={isDeleting}
            style={{
              padding: '10px 20px',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              padding: '10px 20px',
              background: isDeleting ? 'var(--color-gray-300)' : 'var(--color-red-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
            }}
          >
            {isDeleting ? '취소 중...' : '요청 취소'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// V55: 승인된 요청 취소 모달
// ============================================

interface CancelApprovedModalProps {
  count: number;
  isProcessing: boolean;
  error: string | null;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

function CancelApprovedModal({ count, isProcessing, error, onConfirm, onCancel }: CancelApprovedModalProps) {
  const [reason, setReason] = useState('');
  // V290: 취소 사유 필수 입력 검증
  const [reasonError, setReasonError] = useState<string | null>(null);

  const handleConfirm = () => {
    // V290: 취소 사유 필수 — trim 기준 빈 문자열이면 차단
    if (!reason.trim()) {
      setReasonError('취소 사유를 입력해주세요.');
      return;
    }
    setReasonError(null);
    onConfirm(reason);
  };

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
      onClose={onCancel}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          width: '100%',
          maxWidth: '450px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--color-gray-800)' }}>
          승인된 요청 취소
        </h3>

        <p style={{ fontSize: '14px', color: 'var(--color-gray-600)', marginBottom: '16px' }}>
          선택한 {count}건의 승인된 요청에 대해 취소 승인요청을 생성합니다.
          <br />
          <span style={{ color: 'var(--color-orange-600)', fontWeight: '500' }}>
            ※ 취소 요청은 승인자의 승인이 필요합니다.
          </span>
        </p>

        {/* 취소 사유 입력 */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--color-gray-700)', marginBottom: '6px' }}>
            취소 사유 <span style={{ color: 'var(--color-red-500)' }}>*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => { setReason(e.target.value); setReasonError(null); }}
            placeholder="취소 사유를 입력하세요"
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '10px',
              border: `1px solid ${reasonError ? 'var(--color-red-400)' : 'var(--color-gray-300)'}`,
              borderRadius: '6px',
              fontSize: '14px',
              resize: 'vertical',
            }}
          />
          {reasonError && (
            <p style={{ fontSize: '13px', color: 'var(--color-red-500)', marginTop: '4px' }}>{reasonError}</p>
          )}
        </div>

        {error && (
          <div style={{ background: 'var(--color-red-50)', color: 'var(--color-red-600)', padding: '12px', borderRadius: '6px', marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            style={{
              padding: '10px 20px',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            닫기
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing}
            style={{
              padding: '10px 20px',
              background: isProcessing ? 'var(--color-gray-300)' : 'var(--color-orange-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
            }}
          >
            {isProcessing ? '요청 중...' : '취소 요청'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 헬퍼 컴포넌트
// ============================================

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', marginBottom: '8px' }}>
      <div style={{ flex: '0 0 80px', fontSize: '13px', color: 'var(--color-gray-500)' }}>{label}</div>
      <div style={{ fontSize: '14px', color: 'var(--color-gray-800)' }}>{value}</div>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--color-gray-700)', marginBottom: '6px' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

// ============================================
// V175: 시간 Stepper 입력 컴포넌트
// ============================================

interface TimeStepperInputProps {
  value: string;  // "HH:MM" 형식
  onChange: (value: string) => void;
}

/**
 * V175: 24시간 시간 입력 (stepper 방식)
 * - 시간: 1시간 단위 증감
 * - 분: 10분 단위 증감
 * - 24시간 표기 (00:00 ~ 23:50)
 */
function TimeStepperInput({ value, onChange }: TimeStepperInputProps) {
  const [hours, minutes] = value.split(':').map(Number);

  const adjustHour = (delta: number) => {
    let newHours = hours + delta;
    if (newHours < 0) newHours = 23;
    if (newHours > 23) newHours = 0;
    onChange(`${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`);
  };

  const adjustMinute = (delta: number) => {
    let newMinutes = minutes + delta;
    if (newMinutes < 0) newMinutes = 50;
    if (newMinutes >= 60) newMinutes = 0;
    onChange(`${String(hours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`);
  };

  const buttonStyle: React.CSSProperties = {
    width: '28px',
    height: '28px',
    border: '1px solid var(--color-gray-300)',
    background: 'var(--color-gray-50)',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-gray-600)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      {/* 시간 stepper */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
        <button type="button" style={buttonStyle} onClick={() => adjustHour(1)}>+</button>
        <div style={{
          width: '40px',
          textAlign: 'center',
          padding: '6px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: 'var(--color-gray-800)',
        }}>
          {String(hours).padStart(2, '0')}
        </div>
        <button type="button" style={buttonStyle} onClick={() => adjustHour(-1)}>−</button>
      </div>

      <span style={{ fontSize: '20px', fontWeight: '600', color: 'var(--color-gray-500)' }}>:</span>

      {/* 분 stepper */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
        <button type="button" style={buttonStyle} onClick={() => adjustMinute(10)}>+</button>
        <div style={{
          width: '40px',
          textAlign: 'center',
          padding: '6px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: 'var(--color-gray-800)',
        }}>
          {String(minutes).padStart(2, '0')}
        </div>
        <button type="button" style={buttonStyle} onClick={() => adjustMinute(-10)}>−</button>
      </div>
    </div>
  );
}

/**
 * V175: 날짜 다음날 계산
 */
function getNextDate(dateStr: string): string {
  const date = new Date(dateStr);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
}
